import { useCallback, useEffect, useMemo, useState } from "react";
import { CheckCircle2, Copy, ExternalLink, Eye, FileText, Plus, Printer, QrCode, Search, XCircle } from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import QRCode from "qrcode";
import http from "../api/http";
import Modal from "../components/Modal";
import Pagination from "../components/Pagination";
import { useServerPagination } from "../utils/usePagination";
import { formatDate, todayInput } from "../utils/format";
import { crearFiltroFecha } from "../utils/dateFilters";

const presentaciones = [25, 250, 500, 1000, 5000, 10000];
const presentacionesPedidoPeso = [
  { cantidad: 25, unidad: "g" },
  { cantidad: 250, unidad: "g" },
  { cantidad: 500, unidad: "g" },
  { cantidad: 1, unidad: "kg" },
  { cantidad: 5, unidad: "kg" },
  { cantidad: 10, unidad: "kg" },
  { cantidad: 25, unidad: "kg" },
];
const presentacionesPedidoVolumen = [
  { cantidad: 250, unidad: "ml" },
  { cantidad: 500, unidad: "ml" },
  { cantidad: 1, unidad: "l" },
  { cantidad: 5, unidad: "l" },
  { cantidad: 10, unidad: "l" },
];
const ordenTipos = {
  pedidos: { titulo: "Ordenes de pedido", singular: "orden de pedido", codigo: "PED" },
  produccion: { titulo: "Ordenes de produccion", singular: "orden de produccion", codigo: "PROD" },
  fraccionado: { titulo: "Ordenes de fraccionado", singular: "orden de fraccionado", codigo: "FRAC" },
};

const nombreVisibleCliente = (cliente) =>
  `${cliente?.nombre || cliente?.nombreTitular || ""} ${
    cliente?.apellido || cliente?.apellidoTitular || ""
  }`.trim() ||
  cliente?.razonSocial ||
  "";

const itemPedidoVacio = () => ({
  productoId: "",
  codigoSku: "",
  producto: "",
  cantidadPaquetes: "",
  kilosPorPaquete: "",
  tipoMedida: "Peso",
  unidadMedida: "kg",
  unidadCosto: "kg",
  costoUnitario: 0,
  costoTotal: 0,
  precioPorUnidad: 0,
  precioPorPaqueteManual: null,
  precioTotal: 0,
});
const insumoVacio = () => ({
  mercaderiaId: "",
  ordenProduccionId: "",
  origenTipo: "mercaderia",
  codigoIngreso: "",
  productoId: "",
  producto: "",
  tipoProducto: "",
  proveedorId: "",
  proveedor: "",
  rne: "",
  rnpa: "",
  lotePartidaNro: "",
  fechaElaboracion: "",
  fechaVencimiento: "",
  kilosNecesarios: "",
  tipoMedida: "Peso",
  unidadMedida: "kg",
});
const salidaVacia = () => ({ presentacionGramos: 1000, cantidadPaquetes: "" });
const materialEmpaqueVacio = () => ({
  materialId: "",
  nombre: "",
  descripcion: "",
  cantidad: "",
  precioUnitario: "",
  precioTotal: 0,
});

const formulariosIniciales = {
  pedidos: () => ({
    clienteId: "",
    clienteNombre: "",
    clienteTipo: "",
    recetaMedica: {
      nombreMedico: "",
      matriculaMedico: "",
      fechaEmision: "",
      diagnostico: "",
      observaciones: "",
    },
    fecha: todayInput(),
    items: [itemPedidoVacio()],
    ubicacionAsignada: "",
    observaciones: "",
  }),
  produccion: () => ({
    productoTerminado: "",
    cantidad: "",
    cantidadProducir: "",
    rnpa: "",
    lotePartidaNro: "",
    ubicacionAsignada: "",
    fechaVencimiento: "",
    fecha: todayInput(),
    insumos: [insumoVacio()],
    materialesEmpaque: [materialEmpaqueVacio()],
    margenGananciaPorcentaje: "",
    observaciones: "",
  }),
  fraccionado: () => ({
    producto: "",
    ordenProduccionId: "",
    codigoProduccion: "",
    cantidad: "",
    rnpa: "",
    lotePartidaNro: "",
    ubicacionAsignada: "",
    fechaElaboracion: "",
    fechaVencimiento: "",
    fecha: todayInput(),
    salidas: [salidaVacia()],
    observaciones: "",
  }),
};

const numero = (value) => Number(value || 0);
const kgPedido = (item) => numero(item.kilosPorPaquete) * numero(item.cantidadPaquetes);
const kgSalida = (item) => (numero(item.presentacionGramos) * numero(item.cantidadPaquetes)) / 1000;
const dateInputValue = (value) => (value ? String(value).slice(0, 10) : "");
const normalizarProducto = (value) => String(value || "").trim().toLowerCase();
const idReferencia = (value) =>
  String(typeof value === "object" ? value?._id || "" : value || "");
const idProveedorIngreso = (ingreso) => idReferencia(ingreso?.proveedorId);
const idProductoProveedor = (producto) => idReferencia(producto?.productoCatalogoId);
const unidadesConversion = {
  mg: { tipoMedida: "Peso", factor: 0.000001 },
  g: { tipoMedida: "Peso", factor: 0.001 },
  kg: { tipoMedida: "Peso", factor: 1 },
  ml: { tipoMedida: "Volumen", factor: 0.001 },
  l: { tipoMedida: "Volumen", factor: 1 },
};
const normalizarUnidadMedida = (value, fallback = "kg") => {
  const unidad = String(value || "").trim().toLowerCase();
  const aliases = { gr: "g", kilo: "kg", kilos: "kg", litro: "l", litros: "l" };
  return unidadesConversion[unidad] ? unidad : aliases[unidad] || fallback;
};
const convertirUnidad = (cantidad, desde, hasta) => {
  const unidadDesde = normalizarUnidadMedida(desde, hasta);
  const unidadHasta = normalizarUnidadMedida(hasta, unidadDesde);
  const configDesde = unidadesConversion[unidadDesde];
  const configHasta = unidadesConversion[unidadHasta];
  if (!configDesde || !configHasta || configDesde.tipoMedida !== configHasta.tipoMedida) {
    return Math.round(numero(cantidad) * 1000000) / 1000000;
  }
  return Math.round(((numero(cantidad) * configDesde.factor) / configHasta.factor) * 1000000) / 1000000;
};
const unidadPrecioProducto = (tipoMedida) => tipoMedida === "Volumen" ? "l" : "kg";
const etiquetaUnidadCosto = (unidad) => String(unidad || "kg").toLowerCase() === "l" ? "L" : "kg";
const precioCompraIngreso = (ingreso) => {
  const precioBulto = (ingreso?.bultos || []).find((bulto) => numero(bulto.precioPorKg) > 0)?.precioPorKg;
  if (numero(precioBulto) > 0) return numero(precioBulto);

  const unidadCosto = unidadPrecioProducto(
    unidadesConversion[normalizarUnidadMedida(ingreso?.unidadCantidad)]?.tipoMedida || "Peso",
  );
  const cantidadIngreso = convertirUnidad(
    ingreso?.cantidadRecibida,
    ingreso?.unidadCantidad || unidadCosto,
    unidadCosto,
  );
  if (numero(ingreso?.costoTotal) > 0 && cantidadIngreso > 0) {
    return numero(ingreso.costoTotal) / cantidadIngreso;
  }

  const proveedor = typeof ingreso?.proveedorId === "object" ? ingreso.proveedorId : null;
  const productoProveedor = (proveedor?.productos || []).find(
    (producto) => String(producto._id) === String(ingreso?.productoProveedorId || ""),
  );
  return numero(productoProveedor?.precioCompraPorUnidad ?? productoProveedor?.precioPorKg);
};
const precioTotalItemPedido = (item) => {
  const unidadPrecio = unidadPrecioProducto(item.tipoMedida);
  const cantidadUnidadPrecio = convertirUnidad(
    numero(item.kilosPorPaquete) * numero(item.cantidadPaquetes),
    item.unidadMedida || unidadPrecio,
    unidadPrecio,
  );
  return cantidadUnidadPrecio * numero(item.precioPorUnidad);
};
const precioPaqueteItemPedido = (item) => {
  const unidadPrecio = unidadPrecioProducto(item.tipoMedida);
  const cantidadUnidadPrecio = convertirUnidad(
    numero(item.kilosPorPaquete),
    item.unidadMedida || unidadPrecio,
    unidadPrecio,
  );
  return cantidadUnidadPrecio * numero(item.precioPorUnidad);
};
const etiquetaUnidad = (cantidad, unidad) => `${numero(cantidad).toLocaleString("es-AR")} ${unidad || "kg"}`;
const formatoPrecio = (value) => Number(value || 0).toLocaleString("es-AR", {
  style: "currency",
  currency: "ARS",
});
const opcionesPresentacionPedido = (producto) => {
  const tipoMedida = producto?.tipoMedida || "Peso";
  const unidadDestino = producto?.unidadMedida || (tipoMedida === "Volumen" ? "l" : "kg");
  const configuradas = (producto?.presentacionesFraccionamiento || []).map((item) => ({
    value: convertirUnidad(item.cantidad, item.unidadMedida, unidadDestino),
    label: etiquetaUnidad(item.cantidad, item.unidadMedida),
    precioVenta: numero(item.precioVenta),
  })).filter((item) => item.value > 0);
  if (configuradas.length) return configuradas;
  const base = tipoMedida === "Volumen" ? presentacionesPedidoVolumen : presentacionesPedidoPeso;
  return base.map((item) => ({
    value: convertirUnidad(item.cantidad, item.unidad, unidadDestino),
    label: etiquetaUnidad(item.cantidad, item.unidad),
  }));
};
const esFinalCatalogo = (item) =>
  item?.tipo === "Final" || item?.estado === "producido";
const esProducibleCatalogo = (item) =>
  ["Intermedio", "Final"].includes(item?.tipo) || item?.estado === "producido";
const ubicacionSugerida = (producto) => String(producto || "").trim().charAt(0).toUpperCase();
const presentacionPedidoGramos = (item) => Math.round(convertirUnidad(item.kilosPorPaquete, item.unidadMedida || "kg", "kg") * 1000);
const presentacionPedidoTexto = (item) => etiquetaUnidad(item.kilosPorPaquete, item.unidadMedida || "kg");
const formatPresentacion = (gramos) => {
  const value = numero(gramos);
  if (value >= 1000) return `${(value / 1000).toLocaleString("es-AR")}kg`;
  return `${value.toLocaleString("es-AR")}g`;
};
const getOptionValue = (option) => (typeof option === "string" ? option : option.value);
const getOptionLabel = (option) => (typeof option === "string" ? option : option.label);
const desagruparIngresos = (ingresos) => Array.isArray(ingresos)
  ? ingresos.flatMap((ingreso) => ingreso.productos?.length ? ingreso.productos : [ingreso])
  : [];
const ordenTrackingUrl = (tipo, codigo) =>
  `${window.location.origin}/tracking/orden/${tipo}/${encodeURIComponent(codigo || "")}`;

const code128Patterns = [
  "212222", "222122", "222221", "121223", "121322", "131222", "122213", "122312", "132212", "221213",
  "221312", "231212", "112232", "122132", "122231", "113222", "123122", "123221", "223211", "221132",
  "221231", "213212", "223112", "312131", "311222", "321122", "321221", "312212", "322112", "322211",
  "212123", "212321", "232121", "111323", "131123", "131321", "112313", "132113", "132311", "211313",
  "231113", "231311", "112133", "112331", "132131", "113123", "113321", "133121", "313121", "211331",
  "231131", "213113", "213311", "213131", "311123", "311321", "331121", "312113", "312311", "332111",
  "314111", "221411", "431111", "111224", "111422", "121124", "121421", "141122", "141221", "112214",
  "112412", "122114", "122411", "142112", "142211", "241211", "221114", "413111", "241112", "134111",
  "111242", "121142", "121241", "114212", "124112", "124211", "411212", "421112", "421211", "212141",
  "214121", "412121", "111143", "111341", "131141", "114113", "114311", "411113", "411311", "113141",
  "114131", "311141", "411131", "211412", "211214", "211232", "2331112",
];

const code128Codes = (value) => {
  const texto = String(value || "-").split("").map((char) => {
    const code = char.charCodeAt(0);
    return code >= 32 && code <= 126 ? char : "-";
  }).join("");
  const codigos = [104];
  let checksum = 104;
  texto.split("").forEach((char, index) => {
    const code = char.charCodeAt(0) - 32;
    codigos.push(code);
    checksum += code * (index + 1);
  });
  codigos.push(checksum % 103, 106);
  return codigos;
};

const codigoBarrasDataUrl = (value) => {
  const codigos = code128Codes(value);
  const modulo = 2;
  const alto = 58;
  const margen = 12;
  const anchoBarras = codigos.reduce(
    (total, code) => total + code128Patterns[code].split("").reduce((sum, digit) => sum + Number(digit), 0) * modulo,
    0,
  );
  let x = margen;
  const barras = [];
  codigos.forEach((code) => {
    code128Patterns[code].split("").forEach((digit, index) => {
      const ancho = Number(digit) * modulo;
      if (index % 2 === 0) barras.push(`<rect x="${x}" y="0" width="${ancho}" height="${alto}" fill="#111"/>`);
      x += ancho;
    });
  });
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${anchoBarras + margen * 2}" height="${alto}" viewBox="0 0 ${anchoBarras + margen * 2} ${alto}">${barras.join("")}</svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
};

const dibujarCodigoBarrasPDF = (doc, value, x, y, modulo = 0.24, alto = 6) => {
  let cursor = x;
  code128Codes(value).forEach((code) => {
    code128Patterns[code].split("").forEach((digit, index) => {
      const ancho = Number(digit) * modulo;
      if (index % 2 === 0) doc.rect(cursor, y, ancho, alto, "F");
      cursor += ancho;
    });
  });
};

const salidasPorTipo = (salidas = []) => {
  const map = new Map();
  salidas.forEach((salida) => {
    const key = Number(salida.presentacionGramos || 0);
    const actual = map.get(key) || { presentacionGramos: key, cantidadPaquetes: 0, kilosTotales: 0 };
    actual.cantidadPaquetes += numero(salida.cantidadPaquetes);
    actual.kilosTotales += numero(salida.kilosTotales);
    map.set(key, actual);
  });
  return Array.from(map.values()).sort((a, b) => a.presentacionGramos - b.presentacionGramos);
};

const generarRotuloOrdenPDF = async (tipo, orden, salida = null, index = 0) => {
  const codigo = orden.codigo || "-";
  const sku = orden.skuProducto || codigo;
  const qrDataUrl = await QRCode.toDataURL(ordenTrackingUrl(tipo, codigo), { margin: 1, width: 180 });
  const doc = new jsPDF({ unit: "mm", format: [100, 70], orientation: "landscape" });
  const titulo = tipo === "produccion" ? "ORDEN DE PRODUCCION" : "ORDEN DE FRACCIONAMIENTO";
  const producto = tipo === "produccion" ? orden.productoTerminado : orden.producto;
  const unidadOrden = orden.unidadMedida || "kg";
  const cantidad = tipo === "produccion"
    ? `${orden.kilosProducidos || orden.cantidadProducir || 0} ${unidadOrden}`
    : `${salida?.cantidadPaquetes || 0} paquetes ${formatPresentacion(salida?.presentacionGramos)}`;
  const kilos = tipo === "produccion"
    ? `${orden.kilosProducidos || 0} ${unidadOrden}`
    : `${salida?.kilosTotales || 0} kg`;
  const lote = orden.lotePartidaNro || "-";
  const vto = formatDate(orden.fechaVencimiento);

  doc.setProperties({ title: `Rotulo ${codigo}` });
  doc.setDrawColor(20);
  doc.rect(3, 3, 94, 64);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(6);
  doc.setTextColor(85);
  doc.text(titulo, 6, 8);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.setTextColor(10);
  doc.text(codigo, 6, 14);
  doc.setFontSize(7.5);
  doc.text(tipo === "fraccionado" ? `SALIDA ${index + 1}` : "PRODUCTO FINALIZADO", 77, 10, { align: "center" });
  doc.rect(6, 17, 88, 10);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(5.8);
  doc.setTextColor(85);
  doc.text("PRODUCTO", 8, 21);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(10);
  doc.text(producto || "-", 8, 25, { maxWidth: 83 });

  const dato = (label, value, x, y) => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(5.8);
    doc.setTextColor(85);
    doc.text(label.toUpperCase(), x, y);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.4);
    doc.setTextColor(20);
    doc.text(String(value || "-"), x, y + 3.8, { maxWidth: 27 });
  };
  dato("Lote / partida", lote, 6, 33);
  dato("Vencimiento", vto, 37, 33);
  dato("Ubicacion", orden.ubicacionAsignada || "-", 68, 33);
  dato("Cantidad", cantidad, 6, 43);
  dato("Cantidad total", kilos, 37, 43);
  if (tipo === "fraccionado") dato("OP origen", orden.codigoProduccion, 68, 43);
  doc.setFillColor(0, 0, 0);
  dibujarCodigoBarrasPDF(doc, sku, 6, 57, 0.22, 6);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(6.5);
  doc.text(sku, 6, 66);
  doc.addImage(qrDataUrl, "PNG", 78, 49, 16, 16);
  if (typeof doc.autoPrint === "function") doc.autoPrint();
  window.open(doc.output("bloburl"), "_blank", "noopener,noreferrer");
};

const generarOrdenPDF = (tipo, orden) => {
  const config = ordenTipos[tipo];
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text(config.titulo.toUpperCase(), 14, 18);
  doc.setFontSize(10);
  doc.text(`Codigo: ${orden.codigo || "-"}`, 14, 27);
  doc.text(`Fecha: ${formatDate(orden.fecha)}`, 14, 34);
  doc.text(`Estado: ${orden.estado || "pendiente"}`, 150, 27);

  let rows = [];
  let head = [];
  if (tipo === "pedidos") {
    doc.text(`Cliente: ${orden.clienteNombre || "-"}`, 14, 42);
    doc.text(`Precio total: ${formatoPrecio(orden.precioTotal ?? (orden.items || []).reduce((total, item) => total + precioTotalItemPedido(item), 0))}`, 14, 49);
    head = [["Producto", "SKU", "Presentacion", "Paquetes", "Cantidad total", "Precio por paquete", "Subtotal"]];
    rows = (orden.items || []).map((item) => [
      item.producto,
      item.codigoSku || "-",
      presentacionPedidoTexto(item),
      item.cantidadPaquetes,
      `${item.kilosTotales || kgPedido(item)} ${item.unidadMedida || "kg"}`,
      formatoPrecio(precioPaqueteItemPedido(item)),
      formatoPrecio(item.precioTotal ?? precioTotalItemPedido(item)),
    ]);
  }
  if (tipo === "produccion") {
    doc.text(`Producto terminado: ${orden.productoTerminado || "-"}`, 14, 42);
    doc.text(`Cantidad producida: ${orden.kilosProducidos || orden.cantidadProducir || 0} ${orden.unidadMedida || "kg"}`, 14, 49);
    doc.text(`RNPA: ${orden.rnpa || "-"}`, 14, 56);
    doc.text(`Lote / partida: ${orden.lotePartidaNro || "-"}`, 90, 56);
    doc.text(`Vto.: ${formatDate(orden.fechaVencimiento)}`, 14, 63);
    head = [["Ingreso", "Materia prima", "Proveedor", "RNE", "RNPA", "Lote / partida", "Vto", "Cantidad necesaria"]];
    rows = (orden.insumos || []).map((item) => [
      item.codigoIngreso || "-",
      item.producto || "-",
      item.proveedor || "-",
      item.rne || "-",
      item.rnpa || "-",
      item.lotePartidaNro || "-",
      formatDate(item.fechaVencimiento),
      `${item.kilosNecesarios || item.kilos || 0} ${item.unidadMedida || "kg"}`,
    ]);
  }
  if (tipo === "fraccionado") {
    doc.text(`Producto: ${orden.producto || "-"}`, 14, 42);
    if (orden.codigoProduccion) doc.text(`OP origen: ${orden.codigoProduccion}`, 90, 42);
    doc.text(`Cantidad: ${orden.cantidad || orden.kilosAFraccionar || 0}`, 90, 49);
    doc.text(`RNPA: ${orden.rnpa || "-"}`, 14, 56);
    doc.text(`Lote / partida: ${orden.lotePartidaNro || "-"}`, 90, 56);
    doc.text(`Elab.: ${formatDate(orden.fechaElaboracion)}`, 14, 63);
    doc.text(`Vto.: ${formatDate(orden.fechaVencimiento)}`, 90, 63);
    head = [["Presentacion", "Paquetes", "Kg totales"]];
    rows = (orden.salidas || []).map((item) => [
      formatPresentacion(item.presentacionGramos),
      item.cantidadPaquetes,
      item.kilosTotales,
    ]);
  }

  autoTable(doc, {
    startY: ["fraccionado", "produccion"].includes(tipo) ? 72 : 58,
    head,
    body: rows,
    theme: "grid",
    headStyles: { fillColor: [0, 122, 70] },
    styles: { fontSize: 8 },
  });

  if (tipo === "produccion") {
    const unidadCosto = orden.unidadCostoProduccion || unidadPrecioProducto(orden.tipoMedida);
    autoTable(doc, {
      startY: (doc.lastAutoTable?.finalY || 72) + 7,
      head: [["Resumen de costos", "Importe"]],
      body: [
        [`Costo de produccion de 1 ${unidadCosto}`, formatoPrecio(orden.costoProduccionPorUnidad)],
        ["Costo de la cantidad producida", formatoPrecio(orden.costoProduccionLote)],
        ["Costo de materiales", formatoPrecio(orden.costoMateriales)],
        ["Costo total", formatoPrecio(orden.costoTotal)],
        ["Margen de ganancia", `${numero(orden.margenGananciaPorcentaje).toLocaleString("es-AR")}%`],
        ["Precio de venta calculado", formatoPrecio(orden.precioVentaCalculado)],
      ],
      theme: "grid",
      headStyles: { fillColor: [0, 122, 70] },
      styles: { fontSize: 9 },
      columnStyles: { 1: { halign: "right", fontStyle: "bold" } },
    });
  }

  if (orden.observaciones) {
    const y = (doc.lastAutoTable?.finalY || 58) + 10;
    doc.setFont("helvetica", "bold");
    doc.text("Observaciones", 14, y);
    doc.setFont("helvetica", "normal");
    doc.text(doc.splitTextToSize(orden.observaciones, 180), 14, y + 6);
  }

  doc.save(`${config.codigo}_${orden.codigo || "orden"}.pdf`);
};

export default function Operaciones() {
  const [stocks, setStocks] = useState({ materiaPrima: [], terminados: [], fraccionados: [] });
  const [stocksListado, setStocksListado] = useState({ materiaPrima: [], terminados: [], fraccionados: [] });
  const [busquedaStock, setBusquedaStock] = useState("");
  const [proveedorStock, setProveedorStock] = useState("");
  const [productosCatalogo, setProductosCatalogo] = useState([]);
  const [ingresosMercaderia, setIngresosMercaderia] = useState([]);
  const [ordenesProduccion, setOrdenesProduccion] = useState([]);
  const [ordenesFraccionado, setOrdenesFraccionado] = useState([]);
  const [pedidosPendientes, setPedidosPendientes] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [materialesProduccion, setMaterialesProduccion] = useState([]);
  const [tipoModal, setTipoModal] = useState("");
  const [formulario, setFormulario] = useState(null);
  const [modalPedidosPendientes, setModalPedidosPendientes] = useState(false);
  const [pedidoAsignacion, setPedidoAsignacion] = useState(null);
  const [stockLotes, setStockLotes] = useState(null);
  const [alertaReceta, setAlertaReceta] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [mensaje, setMensaje] = useState("");
  const [error, setError] = useState("");
  const [actualizacionStocks, setActualizacionStocks] = useState(0);
  const paginacionMateriaPrima = useServerPagination();
  const paginacionTerminados = useServerPagination();
  const paginacionFraccionados = useServerPagination();

  const productosProducidos = useMemo(
    () => productosCatalogo
      .filter(esProducibleCatalogo)
      .map((item) => ({
        value: item.nombre,
        label: item.nombre,
        receta: item.receta || [],
        materialesProduccion: item.materialesProduccion || [],
        recetaRendimiento: item.recetaRendimiento,
        tipoMedida: item.tipoMedida,
        margenGananciaPorcentaje: item.margenGananciaPorcentaje ?? 0,
      })),
    [productosCatalogo],
  );
  const productosTerminados = useMemo(
    () => stocks.terminados.map((item) => item.producto),
    [stocks.terminados],
  );
  const productosVenta = useMemo(
    () => productosCatalogo
      .filter(esFinalCatalogo)
      .map((item) => ({
        value: item.nombre,
        label: item.nombre,
        productoId: item._id,
        codigoSku: item.codigoSku || "",
        tipoMedida: item.tipoMedida || "Peso",
        unidadMedida: item.unidadMedida || "kg",
        precioVentaPorUnidad: numero(item.precioVentaPorUnidad),
        presentacionesFraccionamiento: item.presentacionesFraccionamiento || [],
      }))
      .sort((a, b) => a.label.localeCompare(b.label)),
    [productosCatalogo],
  );
  const filtrarStockPorProveedor = (proveedorId) => {
    setProveedorStock(proveedorId);
    paginacionMateriaPrima.resetPage();
  };

  const cargarTodo = async () => {
    try {
      setCargando(true);
      setError("");
      const [stocksRes, clientesRes, productosRes, ingresosRes, pedidosRes, produccionRes, fraccionadoRes, materialesRes, proveedoresRes] =
        await Promise.all([
          http.get("/operaciones/stocks"),
          http.get("/clientes"),
          http.get("/productos?activo=true"),
          http.get("/mercaderia"),
          http.get("/operaciones/ordenes/pedidos"),
          http.get("/operaciones/ordenes/produccion"),
          http.get("/operaciones/ordenes/fraccionado"),
          http.get("/materiales-produccion"),
          http.get("/proveedores"),
        ]);
      setStocks(stocksRes.data);
      setClientes(Array.isArray(clientesRes.data) ? clientesRes.data : []);
      setProductosCatalogo(Array.isArray(productosRes.data) ? productosRes.data : []);
      setIngresosMercaderia(desagruparIngresos(ingresosRes.data));
      setPedidosPendientes((Array.isArray(pedidosRes.data) ? pedidosRes.data : []).filter((pedido) => pedido.estado === "pendiente"));
      setOrdenesProduccion(Array.isArray(produccionRes.data) ? produccionRes.data : []);
      setOrdenesFraccionado(Array.isArray(fraccionadoRes.data) ? fraccionadoRes.data : []);
      setMaterialesProduccion(Array.isArray(materialesRes.data) ? materialesRes.data : []);
      setProveedores(Array.isArray(proveedoresRes.data) ? proveedoresRes.data : []);
      setActualizacionStocks((actual) => actual + 1);
    } catch (err) {
      setError(err.response?.data?.error || "No se pudieron cargar las operaciones.");
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargarTodo();
  }, []);

  useEffect(() => {
    const timeoutId = window.setTimeout(async () => {
      try {
        const { data } = await http.get("/operaciones/stocks", {
          params: {
            paginate: true,
            q: busquedaStock.trim() || undefined,
            proveedorId: proveedorStock || undefined,
            materiaPrimaPage: paginacionMateriaPrima.page,
            materiaPrimaLimit: paginacionMateriaPrima.pageSize,
            terminadosPage: paginacionTerminados.page,
            terminadosLimit: paginacionTerminados.pageSize,
            fraccionadosPage: paginacionFraccionados.page,
            fraccionadosLimit: paginacionFraccionados.pageSize,
          },
        });
        setStocksListado({
          materiaPrima: Array.isArray(data?.materiaPrima?.items) ? data.materiaPrima.items : [],
          terminados: Array.isArray(data?.terminados?.items) ? data.terminados.items : [],
          fraccionados: Array.isArray(data?.fraccionados?.items) ? data.fraccionados.items : [],
        });
        paginacionMateriaPrima.applyResponse(data?.materiaPrima);
        paginacionTerminados.applyResponse(data?.terminados);
        paginacionFraccionados.applyResponse(data?.fraccionados);
      } catch (err) {
        setError(err.response?.data?.error || "No se pudo cargar el stock.");
      }
    }, 250);
    return () => window.clearTimeout(timeoutId);
  }, [
    actualizacionStocks,
    busquedaStock,
    proveedorStock,
    paginacionMateriaPrima.page,
    paginacionMateriaPrima.pageSize,
    paginacionTerminados.page,
    paginacionTerminados.pageSize,
    paginacionFraccionados.page,
    paginacionFraccionados.pageSize,
  ]);

  const cargarLotesItem = async (item, filtrarProveedor = false) => {
    if (item.lotesCargados) return item;
    const { data } = await http.get("/operaciones/stocks/lotes", {
      params: {
        tipo: item.tipoStock,
        codigoStock: item.codigoStock,
        presentacion: item.presentacion ?? item.presentacionGramos ?? 0,
        ...(filtrarProveedor && proveedorStock && item.tipoStock === "materia_prima"
          ? { proveedorId: proveedorStock }
          : {}),
      },
    });
    return {
      ...item,
      lotes: Array.isArray(data) ? data : [],
      lotesCargados: true,
    };
  };

  const cargarLotesOperativos = async () => {
    const [materiaPrima, terminados, fraccionados] = await Promise.all([
      Promise.all((stocks.materiaPrima || []).map(cargarLotesItem)),
      Promise.all((stocks.terminados || []).map(cargarLotesItem)),
      Promise.all((stocks.fraccionados || []).map(cargarLotesItem)),
    ]);
    const stocksConLotes = { ...stocks, materiaPrima, terminados, fraccionados };
    setStocks(stocksConLotes);
    return stocksConLotes;
  };

  const abrirLotesStock = (tipo, item) => {
    setError("");
    setStockLotes({ tipo, item, proveedorId: proveedorStock || "" });
  };

  const abrirOrden = async (tipo) => {
    if (["produccion", "fraccionado"].includes(tipo)) {
      try {
        setCargando(true);
        await cargarLotesOperativos();
      } catch (err) {
        setError(err.response?.data?.error || "No se pudieron cargar los lotes operativos.");
        setCargando(false);
        return;
      } finally {
        setCargando(false);
      }
    }
    setTipoModal(tipo);
    setFormulario(formulariosIniciales[tipo]());
    setAlertaReceta(null);
    setMensaje("");
    setError("");
  };

  const cerrarModal = () => {
    setTipoModal("");
    setFormulario(null);
    setAlertaReceta(null);
  };

  const setCampo = (name, value) => {
    setFormulario((prev) => ({ ...prev, [name]: value }));
  };

  const setItem = (key, index, name, value) => {
    setFormulario((prev) => ({
      ...prev,
      [key]: prev[key].map((item, itemIndex) =>
        itemIndex === index ? { ...item, [name]: value } : item,
      ),
    }));
  };

  const agregarItem = (key, factory) => {
    setFormulario((prev) => ({ ...prev, [key]: [...prev[key], factory()] }));
  };

  const quitarItem = (key, index, factory) => {
    setFormulario((prev) => ({
      ...prev,
      [key]: prev[key].length > 1 ? prev[key].filter((_, itemIndex) => itemIndex !== index) : [factory()],
    }));
  };

  const guardarOrden = async (event) => {
    event.preventDefault();
    const accion = event.nativeEvent?.submitter?.value || "guardar";
    if (tipoModal === "produccion" && !formulario.productoTerminado) {
      setError("Selecciona un producto a producir de la lista.");
      return;
    }
    if (tipoModal === "produccion" && alertaReceta?.faltantes?.length) {
      setError("La receta tiene ingredientes faltantes. Ajusta los ingredientes antes de guardar.");
      return;
    }
    if (tipoModal === "produccion" && (formulario.insumos || []).some((item) => item.kilosNecesarios && !item.mercaderiaId && !item.ordenProduccionId)) {
      setError("Selecciona un lote u origen valido para cada ingrediente cargado.");
      return;
    }
    if (tipoModal === "produccion" && (formulario.materialesEmpaque || []).some((item) => numero(item.cantidad) > 0 && !item.materialId)) {
      setError("Selecciona un material valido para cada cantidad cargada.");
      return;
    }
    if (tipoModal === "produccion") {
      const materialSinStock = (formulario.materialesEmpaque || []).find((item) => {
        if (!item.materialId || numero(item.cantidad) <= 0) return false;
        const material = materialesProduccion.find((catalogo) => String(catalogo._id) === String(item.materialId));
        return numero(item.cantidad) > numero(material?.stockActual);
      });
      if (materialSinStock) {
        setError(`Stock insuficiente de ${materialSinStock.nombre || "material"}.`);
        return;
      }
    }
    if (tipoModal === "fraccionado" && !formulario.producto) {
      setError("Selecciona un producto terminado de la lista.");
      return;
    }
    if (["produccion", "fraccionado"].includes(tipoModal) && !window.confirm(`Crear ${ordenTipos[tipoModal].singular}?`)) return;
    try {
      setCargando(true);
      setError("");
      const { data: ordenCreada } = await http.post(`/operaciones/ordenes/${tipoModal}`, formulario);
      if (accion === "finalizar") {
        await http.patch(`/operaciones/ordenes/${tipoModal}/${ordenCreada._id}/finalizar`);
        window.dispatchEvent(new Event("notificaciones:actualizar"));
        setMensaje(`Se genero y finalizo la ${ordenTipos[tipoModal].singular}.`);
      } else {
        setMensaje(`Se genero la ${ordenTipos[tipoModal].singular}.`);
      }
      cerrarModal();
      await cargarTodo();
    } catch (err) {
      setError(err.response?.data?.error || "No se pudo generar la orden.");
    } finally {
      setCargando(false);
    }
  };

  const finalizarPedidoPendiente = async (pedido) => {
    try {
      setCargando(true);
      await cargarLotesOperativos();
      setModalPedidosPendientes(false);
      setPedidoAsignacion(pedido);
    } catch (err) {
      setError(err.response?.data?.error || "No se pudieron cargar los lotes del pedido.");
    } finally {
      setCargando(false);
    }
  };

  const confirmarFinalizacionPedido = async (pedido, trazabilidadAsignada) => {
    try {
      setCargando(true);
      setError("");
      await http.patch(`/operaciones/ordenes/pedidos/${pedido._id}/finalizar`, { trazabilidadAsignada });
      window.dispatchEvent(new Event("notificaciones:actualizar"));
      setMensaje(`${pedido.codigo} finalizada.`);
      setPedidoAsignacion(null);
      await cargarTodo();
    } catch (err) {
      throw new Error(err.response?.data?.error || "No se pudo finalizar la orden de pedido.");
    } finally {
      setCargando(false);
    }
  };

  const realizarAccionPedido = async (accion) => {
    try {
      setCargando(true);
      await cargarLotesOperativos();
    } catch (err) {
      setError(err.response?.data?.error || "No se pudieron cargar los lotes operativos.");
      setCargando(false);
      return;
    } finally {
      setCargando(false);
    }
    setModalPedidosPendientes(false);
    setAlertaReceta(null);
    setMensaje("");
    setError("");

    if (accion.tipo === "fraccionado") {
      setTipoModal("fraccionado");
      setFormulario({
        ...formulariosIniciales.fraccionado(),
        producto: accion.producto,
        ubicacionAsignada: ubicacionSugerida(accion.producto),
        cantidad: accion.kilosAFraccionar,
        salidas: [
          {
            presentacionGramos: accion.presentacionGramos,
            cantidadPaquetes: accion.cantidadPaquetes,
          },
        ],
      });
      return;
    }

    if (accion.tipo === "produccion") {
      const producto = productosCatalogo.find(
        (item) => normalizarProducto(item.nombre) === normalizarProducto(accion.producto),
      );
      const rendimiento = numero(producto?.recetaRendimiento?.cantidad);
      setTipoModal("produccion");
      setFormulario({
        ...formulariosIniciales.produccion(),
        productoTerminado: accion.producto,
        ubicacionAsignada: ubicacionSugerida(accion.producto),
        cantidadProducir: numero(accion.cantidadPaquetes25kg) * rendimiento,
      });
    }
  };

  return (
    <div>
      <div className="page-header operations-stock-toolbar">
        <div className="search stock-product-search">
          <Search size={18} />
          <input
            type="search"
            value={busquedaStock}
            onChange={(event) => {
              setBusquedaStock(event.target.value);
              paginacionMateriaPrima.resetPage();
              paginacionTerminados.resetPage();
              paginacionFraccionados.resetPage();
            }}
            placeholder="Buscar producto en stock..."
            aria-label="Buscar producto en stock"
          />
        </div>
        <div className="field stock-provider-filter">
          <label htmlFor="stock-proveedor">Proveedor de materia prima</label>
          <select
            id="stock-proveedor"
            value={proveedorStock}
            onChange={(event) => filtrarStockPorProveedor(event.target.value)}
          >
            <option value="">Todos los proveedores</option>
            {proveedores.map((proveedor) => (
              <option key={proveedor._id} value={proveedor._id}>
                {proveedor.razonSocial || proveedor.nombreFantasia || "Proveedor"}
              </option>
            ))}
          </select>
        </div>
        <div className="header-actions">
          <button className="btn" onClick={() => setModalPedidosPendientes(true)}>
            <FileText size={17} />
            Pedidos pendientes
          </button>
        </div>
      </div>

      {mensaje && <div className="alert alert-ok">{mensaje}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      <section className="ops-stock-grid ops-stock-grid-compact">
        <StockMateria
          items={stocksListado.materiaPrima}
          paginacion={paginacionMateriaPrima}
          cargando={cargando}
          onVerLotes={(item) => abrirLotesStock("Materia prima", item)}
        />
        <StockTerminado
          items={stocksListado.terminados}
          paginacion={paginacionTerminados}
          onCrearProduccion={() => abrirOrden("produccion")}
          onVerLotes={(item) => abrirLotesStock("Mercaderia a granel", item)}
        />
        <StockFraccionado
          items={stocksListado.fraccionados}
          paginacion={paginacionFraccionados}
          onCrearFraccionado={() => abrirOrden("fraccionado")}
          onVerLotes={(item) => abrirLotesStock("Mercaderia fraccionada", item)}
        />
      </section>

      <OrdenModal
        tipo={tipoModal}
        formulario={formulario}
        clientes={clientes}
        productosTerminados={productosTerminados}
        productosProducidos={productosProducidos}
        productosCatalogo={productosCatalogo}
        proveedores={proveedores}
        productosVenta={productosVenta}
        ordenesProduccion={ordenesProduccion}
        ingresosMercaderia={ingresosMercaderia}
        stocks={stocks}
        materialesProduccion={materialesProduccion}
        cargando={cargando}
        error={error}
        recipeAlert={alertaReceta}
        onError={setError}
        onRecipeAlert={setAlertaReceta}
        onClose={cerrarModal}
        onSubmit={guardarOrden}
        setCampo={setCampo}
        setItem={setItem}
        agregarItem={agregarItem}
        quitarItem={quitarItem}
      />
      <PedidosPendientesModal
        abierto={modalPedidosPendientes}
        pedidos={pedidosPendientes}
        stocks={stocks}
        ordenesProduccion={ordenesProduccion}
        ordenesFraccionado={ordenesFraccionado}
        cargando={cargando}
        onFinalizarPedido={finalizarPedidoPendiente}
        onRealizarAccion={realizarAccionPedido}
        onClose={() => setModalPedidosPendientes(false)}
      />
      <StockLotesModal data={stockLotes} onClose={() => setStockLotes(null)} />
      <PedidoFinalizarModal
        pedido={pedidoAsignacion}
        stocks={stocks}
        cargando={cargando}
        onClose={() => setPedidoAsignacion(null)}
        onConfirm={confirmarFinalizacionPedido}
      />
    </div>
  );
}

export function OperacionesOrdenes({ tipo }) {
  const [ordenesPendientes, setOrdenesPendientes] = useState([]);
  const [ordenesFinalizadas, setOrdenesFinalizadas] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [productosCatalogo, setProductosCatalogo] = useState([]);
  const [ingresosMercaderia, setIngresosMercaderia] = useState([]);
  const [ordenesProduccion, setOrdenesProduccion] = useState([]);
  const [materialesProduccion, setMaterialesProduccion] = useState([]);
  const [stocks, setStocks] = useState({ materiaPrima: [], terminados: [], fraccionados: [] });
  const [formularioPedido, setFormularioPedido] = useState(null);
  const [tipoModalOrden, setTipoModalOrden] = useState(tipo);
  const [ordenDetalle, setOrdenDetalle] = useState(null);
  const [ordenTracking, setOrdenTracking] = useState(null);
  const [ordenTrackingQr, setOrdenTrackingQr] = useState("");
  const [ordenRotulos, setOrdenRotulos] = useState(null);
  const [pedidoAsignacion, setPedidoAsignacion] = useState(null);
  const [alertaReceta, setAlertaReceta] = useState(null);
  const [filtroFinalizadas, setFiltroFinalizadas] = useState(crearFiltroFecha);
  const [busquedaCodigo, setBusquedaCodigo] = useState("");
  const [busquedaCliente, setBusquedaCliente] = useState("");
  const [buscarFueraFecha, setBuscarFueraFecha] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [mensaje, setMensaje] = useState("");
  const [error, setError] = useState("");
  const [actualizacionListados, setActualizacionListados] = useState(0);
  const paginacionPendientes = useServerPagination();
  const paginacionFinalizadas = useServerPagination();

  const cargarOrdenes = useCallback(async () => {
    try {
      setCargando(true);
      setError("");
      const [ordenesRes, clientesRes, productosRes, stocksRes, ingresosRes, produccionRes, materialesRes, proveedoresRes] = await Promise.all([
        http.get(`/operaciones/ordenes/${tipo}`),
        http.get("/clientes"),
        http.get("/productos?activo=true"),
        http.get("/operaciones/stocks"),
        http.get("/mercaderia"),
        tipo === "produccion" ? Promise.resolve(null) : http.get("/operaciones/ordenes/produccion"),
        http.get("/materiales-produccion"),
        http.get("/proveedores"),
      ]);
      setClientes(Array.isArray(clientesRes.data) ? clientesRes.data : []);
      setProductosCatalogo(Array.isArray(productosRes.data) ? productosRes.data : []);
      setStocks(stocksRes.data || { materiaPrima: [], terminados: [], fraccionados: [] });
      setIngresosMercaderia(desagruparIngresos(ingresosRes.data));
      setOrdenesProduccion(tipo === "produccion" ? (Array.isArray(ordenesRes.data) ? ordenesRes.data : []) : (Array.isArray(produccionRes?.data) ? produccionRes.data : []));
      setMaterialesProduccion(Array.isArray(materialesRes.data) ? materialesRes.data : []);
      setProveedores(Array.isArray(proveedoresRes.data) ? proveedoresRes.data : []);
      setActualizacionListados((actual) => actual + 1);
    } catch (err) {
      setError(err.response?.data?.error || "No se pudieron cargar las ordenes.");
    } finally {
      setCargando(false);
    }
  }, [tipo]);

  const productosProducidos = useMemo(
    () => productosCatalogo
      .filter(esProducibleCatalogo)
      .map((item) => ({
        value: item.nombre,
        label: item.nombre,
        receta: item.receta || [],
        materialesProduccion: item.materialesProduccion || [],
        recetaRendimiento: item.recetaRendimiento,
        tipoMedida: item.tipoMedida,
        margenGananciaPorcentaje: item.margenGananciaPorcentaje ?? 0,
      })),
    [productosCatalogo],
  );
  const productosTerminados = useMemo(
    () => stocks.terminados.map((item) => item.producto),
    [stocks.terminados],
  );
  const productosVenta = useMemo(
    () => productosCatalogo
      .filter(esFinalCatalogo)
      .map((item) => ({
        value: item.nombre,
        label: item.nombre,
        productoId: item._id,
        codigoSku: item.codigoSku || "",
        tipoMedida: item.tipoMedida || "Peso",
        unidadMedida: item.unidadMedida || "kg",
        precioVentaPorUnidad: numero(item.precioVentaPorUnidad),
        presentacionesFraccionamiento: item.presentacionesFraccionamiento || [],
      }))
      .sort((a, b) => a.label.localeCompare(b.label)),
    [productosCatalogo],
  );
  const simulacionPedidoAutomatica = useMemo(
    () => tipo === "pedidos"
      ? calcularRecetasPedido(formularioPedido, productosCatalogo, proveedores)
      : { productos: [], totalVenta: 0 },
    [tipo, formularioPedido, productosCatalogo, proveedores],
  );
  const preciosPedidoAutomaticos = useMemo(
    () => preciosDeSimulacion(simulacionPedidoAutomatica),
    [simulacionPedidoAutomatica],
  );
  const precioAutomaticoParaItem = (item) => {
    const productoCatalogo = productosCatalogo.find(
      (producto) =>
        String(producto._id || "") === String(item.productoId || "") ||
        normalizarProducto(producto.nombre) === normalizarProducto(item.producto),
    );
    if (numero(productoCatalogo?.precioVentaPorUnidad) > 0) {
      return { precioPorUnidad: numero(productoCatalogo.precioVentaPorUnidad) };
    }
    return preciosPedidoAutomaticos.find(
      (producto) =>
        String(producto.productoId || "") === String(item.productoId || "") ||
        normalizarProducto(producto.nombre) === normalizarProducto(item.producto),
    );
  };
  const itemsPedidoConPrecios = () => (formularioPedido?.items || []).map((item) => {
    const automatico = precioAutomaticoParaItem(item);
    const cantidadPaqueteUnidadPrecio = convertirUnidad(
      numero(item.kilosPorPaquete),
      item.unidadMedida,
      item.tipoMedida === "Volumen" ? "l" : "kg",
    );
    const tienePrecioManual = item.precioPorPaqueteManual !== null &&
      item.precioPorPaqueteManual !== undefined;
    const precioPorUnidad = tienePrecioManual
      ? (cantidadPaqueteUnidadPrecio > 0
          ? numero(item.precioPorPaqueteManual) / cantidadPaqueteUnidadPrecio
          : 0)
      : (numero(item.precioPorUnidad) > 0
          ? numero(item.precioPorUnidad)
          : numero(automatico?.precioPorUnidad));
    const cantidadUnidadPrecio = convertirUnidad(
      numero(item.kilosPorPaquete) * numero(item.cantidadPaquetes),
      item.unidadMedida,
      item.tipoMedida === "Volumen" ? "l" : "kg",
    );
    return { ...item, precioPorUnidad, precioTotal: cantidadUnidadPrecio * precioPorUnidad };
  });

  useEffect(() => {
    cargarOrdenes();
  }, [cargarOrdenes]);

  useEffect(() => {
    const timeoutId = window.setTimeout(async () => {
      try {
        const paramsBusqueda = {
          q: busquedaCodigo.trim() || undefined,
          cliente: tipo === "pedidos" ? busquedaCliente.trim() || undefined : undefined,
        };
        const [pendientesRes, finalizadasRes] = await Promise.all([
          http.get(`/operaciones/ordenes/${tipo}`, {
            params: {
              ...paramsBusqueda,
              estado: "pendiente",
              page: paginacionPendientes.page,
              limit: paginacionPendientes.pageSize,
            },
          }),
          http.get(`/operaciones/ordenes/${tipo}`, {
            params: {
              ...paramsBusqueda,
              estado: "finalizada",
              buscarFueraFecha: buscarFueraFecha || undefined,
              periodo: filtroFinalizadas.tipo,
              dia: filtroFinalizadas.dia || undefined,
              semana: filtroFinalizadas.semana || undefined,
              mes: filtroFinalizadas.mes || undefined,
              anio: filtroFinalizadas.anio || undefined,
              desde: filtroFinalizadas.desde || undefined,
              hasta: filtroFinalizadas.hasta || undefined,
              page: paginacionFinalizadas.page,
              limit: paginacionFinalizadas.pageSize,
            },
          }),
        ]);
        setOrdenesPendientes(Array.isArray(pendientesRes.data?.items) ? pendientesRes.data.items : []);
        setOrdenesFinalizadas(Array.isArray(finalizadasRes.data?.items) ? finalizadasRes.data.items : []);
        paginacionPendientes.applyResponse(pendientesRes.data);
        paginacionFinalizadas.applyResponse(finalizadasRes.data);
      } catch (err) {
        setError(err.response?.data?.error || "No se pudieron cargar las ordenes.");
      }
    }, 250);
    return () => window.clearTimeout(timeoutId);
  }, [
    actualizacionListados,
    busquedaCliente,
    busquedaCodigo,
    buscarFueraFecha,
    filtroFinalizadas,
    paginacionFinalizadas.page,
    paginacionFinalizadas.pageSize,
    paginacionPendientes.page,
    paginacionPendientes.pageSize,
    tipo,
  ]);

  const cargarLotesOperativosOrdenes = async () => {
    const cargar = async (item) => {
      if (item.lotesCargados) return item;
      const { data } = await http.get("/operaciones/stocks/lotes", {
        params: {
          tipo: item.tipoStock,
          codigoStock: item.codigoStock,
          presentacion: item.presentacion ?? item.presentacionGramos ?? 0,
        },
      });
      return { ...item, lotes: Array.isArray(data) ? data : [], lotesCargados: true };
    };
    const [materiaPrima, terminados, fraccionados] = await Promise.all([
      Promise.all((stocks.materiaPrima || []).map(cargar)),
      Promise.all((stocks.terminados || []).map(cargar)),
      Promise.all((stocks.fraccionados || []).map(cargar)),
    ]);
    const completos = { ...stocks, materiaPrima, terminados, fraccionados };
    setStocks(completos);
    return completos;
  };

  const finalizarOrden = async (orden) => {
    if (tipo === "pedidos") {
      try {
        setCargando(true);
        await cargarLotesOperativosOrdenes();
        setPedidoAsignacion(orden);
      } catch (err) {
        setError(err.response?.data?.error || "No se pudieron cargar los lotes del pedido.");
      } finally {
        setCargando(false);
      }
      return;
    }
    if (!window.confirm(`Finalizar ${orden.codigo}? Esto impacta el stock.`)) return;
    try {
      setCargando(true);
      setError("");
      await http.patch(`/operaciones/ordenes/${tipo}/${orden._id}/finalizar`);
      window.dispatchEvent(new Event("notificaciones:actualizar"));
      setMensaje(`${orden.codigo} finalizada.`);
      await cargarOrdenes();
    } catch (err) {
      setError(err.response?.data?.error || "No se pudo finalizar la orden.");
    } finally {
      setCargando(false);
    }
  };

  const confirmarFinalizacionPedido = async (pedido, trazabilidadAsignada) => {
    try {
      setCargando(true);
      setError("");
      await http.patch(`/operaciones/ordenes/pedidos/${pedido._id}/finalizar`, { trazabilidadAsignada });
      window.dispatchEvent(new Event("notificaciones:actualizar"));
      setMensaje(`${pedido.codigo} finalizada.`);
      setPedidoAsignacion(null);
      await cargarOrdenes();
    } catch (err) {
      throw new Error(err.response?.data?.error || "No se pudo finalizar la orden de pedido.");
    } finally {
      setCargando(false);
    }
  };

  const anularOrden = async (orden) => {
    if (!window.confirm(`Anular ${orden.codigo}?`)) return;
    try {
      setCargando(true);
      setError("");
      await http.patch(`/operaciones/ordenes/${tipo}/${orden._id}/anular`);
      setMensaje(`${orden.codigo} anulada.`);
      await cargarOrdenes();
    } catch (err) {
      setError(err.response?.data?.error || "No se pudo anular la orden.");
    } finally {
      setCargando(false);
    }
  };

  const hayBusqueda = Boolean(
    busquedaCodigo.trim() || (tipo === "pedidos" && busquedaCliente.trim()),
  );
  const skuDeOrden = (orden) => {
    const producto = tipo === "produccion" ? orden.productoTerminado : orden.producto;
    return productosCatalogo.find((item) => normalizarProducto(item.nombre) === normalizarProducto(producto))?.codigoSku || orden.codigo || "";
  };
  const abrirRotulosOrden = (orden) => setOrdenRotulos({ ...orden, skuProducto: skuDeOrden(orden) });

  const abrirTrackingOrden = async (orden) => {
    setOrdenTracking(orden);
    setOrdenTrackingQr("");
    try {
      const dataUrl = await QRCode.toDataURL(ordenTrackingUrl(tipo, orden.codigo), { margin: 2, width: 190 });
      setOrdenTrackingQr(dataUrl);
    } catch {
      setOrdenTrackingQr("");
    }
  };

  const copiarTrackingOrden = async (orden) => {
    try {
      await navigator.clipboard.writeText(ordenTrackingUrl(tipo, orden.codigo));
      setMensaje("Link de tracking copiado.");
    } catch {
      setError("No se pudo copiar el link de tracking.");
    }
  };

  const abrirPedido = async () => {
    if (["produccion", "fraccionado"].includes(tipo)) {
      try {
        setCargando(true);
        await cargarLotesOperativosOrdenes();
      } catch (err) {
        setError(err.response?.data?.error || "No se pudieron cargar los lotes operativos.");
        setCargando(false);
        return;
      } finally {
        setCargando(false);
      }
    }
    setTipoModalOrden(tipo);
    setFormularioPedido(formulariosIniciales[tipo]());
    setAlertaReceta(null);
    setMensaje("");
    setError("");
  };

  const cerrarPedido = () => {
    setFormularioPedido(null);
    setTipoModalOrden(tipo);
    setAlertaReceta(null);
  };

  const setCampoPedido = (name, value) => {
    setFormularioPedido((prev) => ({ ...prev, [name]: value }));
  };

  const setItemPedido = (key, index, name, value) => {
    const invalidaPrecio = tipo === "pedidos" && key === "items" && [
      "productoId",
      "producto",
      "kilosPorPaquete",
      "tipoMedida",
      "unidadMedida",
    ].includes(name);
    setFormularioPedido((prev) => ({
      ...prev,
      [key]: prev[key].map((item, itemIndex) =>
        itemIndex === index
          ? {
              ...item,
              [name]: value,
              ...(invalidaPrecio ? { precioPorUnidad: 0, precioPorPaqueteManual: null, precioTotal: 0 } : {}),
            }
          : item,
      ),
    }));
  };

  const agregarItemPedido = (key, factory) => {
    setFormularioPedido((prev) => ({ ...prev, [key]: [...prev[key], factory()] }));
  };

  const quitarItemPedido = (key, index, factory) => {
    setFormularioPedido((prev) => ({
      ...prev,
      [key]: prev[key].length > 1 ? prev[key].filter((_, itemIndex) => itemIndex !== index) : [factory()],
    }));
  };

  const guardarPedido = async (event) => {
    event.preventDefault();
    const accion = event.nativeEvent?.submitter?.value || "guardar";
    const tipoActual = tipoModalOrden || tipo;
    if (tipoActual === "produccion" && !formularioPedido.productoTerminado) {
      setError("Selecciona un producto a producir de la lista.");
      return;
    }
    if (tipoActual === "produccion" && alertaReceta?.faltantes?.length) {
      setError("La receta tiene ingredientes faltantes. Ajusta los ingredientes antes de guardar.");
      return;
    }
    if (tipoActual === "produccion" && (formularioPedido.insumos || []).some((item) => item.kilosNecesarios && !item.mercaderiaId && !item.ordenProduccionId)) {
      setError("Selecciona un lote u origen valido para cada ingrediente cargado.");
      return;
    }
    if (tipoActual === "produccion" && (formularioPedido.materialesEmpaque || []).some((item) => numero(item.cantidad) > 0 && !item.materialId)) {
      setError("Selecciona un material valido para cada cantidad cargada.");
      return;
    }
    if (tipoActual === "produccion") {
      const materialSinStock = (formularioPedido.materialesEmpaque || []).find((item) => {
        if (!item.materialId || numero(item.cantidad) <= 0) return false;
        const material = materialesProduccion.find((catalogo) => String(catalogo._id) === String(item.materialId));
        return numero(item.cantidad) > numero(material?.stockActual);
      });
      if (materialSinStock) {
        setError(`Stock insuficiente de ${materialSinStock.nombre || "material"}.`);
        return;
      }
    }
    if (tipoActual === "fraccionado" && !formularioPedido.producto) {
      setError("Selecciona un producto terminado de la lista.");
      return;
    }
    const itemsConPrecios = tipoActual === "pedidos" ? itemsPedidoConPrecios() : formularioPedido.items;
    if (tipoActual === "pedidos" && (itemsConPrecios || []).some(
      (item) => item.producto && numero(item.precioPorUnidad) <= 0,
    )) {
      setError("No se pudo calcular el precio de uno o mas productos. Revisa su receta, margen y precios de proveedores.");
      return;
    }
    if (["produccion", "fraccionado"].includes(tipoActual) && !window.confirm(`Crear ${ordenTipos[tipoActual].singular}?`)) return;
    try {
      setCargando(true);
      setError("");
      const payload = tipoActual === "pedidos"
        ? { ...formularioPedido, items: itemsConPrecios }
        : formularioPedido;
      const { data: ordenCreada } = await http.post(`/operaciones/ordenes/${tipoActual}`, payload);
      if (accion === "finalizar" && ["produccion", "fraccionado"].includes(tipoActual)) {
        await http.patch(`/operaciones/ordenes/${tipoActual}/${ordenCreada._id}/finalizar`);
        window.dispatchEvent(new Event("notificaciones:actualizar"));
        setMensaje(`Se genero y finalizo la ${ordenTipos[tipoActual].singular}.`);
      } else {
        setMensaje(`Se genero la ${ordenTipos[tipoActual].singular}.`);
      }
      cerrarPedido();
      await cargarOrdenes();
    } catch (err) {
      setError(err.response?.data?.error || "No se pudo generar la orden de pedido.");
    } finally {
      setCargando(false);
    }
  };

  return (
    <div>
      <div className="page-header actions-only">
        <BuscadorCodigo
          value={busquedaCodigo}
          onChange={(value) => {
            setBusquedaCodigo(value);
            setBuscarFueraFecha(false);
            paginacionPendientes.resetPage();
            paginacionFinalizadas.resetPage();
          }}
          clienteValue={tipo === "pedidos" ? busquedaCliente : undefined}
          onClienteChange={tipo === "pedidos" ? (value) => {
            setBusquedaCliente(value);
            setBuscarFueraFecha(false);
            paginacionPendientes.resetPage();
            paginacionFinalizadas.resetPage();
          } : undefined}
          onBuscar={() => {
            setBuscarFueraFecha(hayBusqueda);
            paginacionPendientes.resetPage();
            paginacionFinalizadas.resetPage();
          }}
        />
        <div className="header-actions">
          <button className="btn btn-primary" onClick={() => abrirPedido()}>
            <Plus size={17} />
            {ordenTipos[tipo].singular}
          </button>
        </div>
      </div>

      {mensaje && <div className="alert alert-ok">{mensaje}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      <div className="ops-orders-grid">
          <OrdenesPanel
            tipo={tipo}
            ordenes={ordenesPendientes}
            paginacion={paginacionPendientes}
            titulo="Ordenes pendientes"
            cargando={cargando}
            onDetalle={(orden) => setOrdenDetalle(orden)}
            onPdf={(orden) => generarOrdenPDF(tipo, orden)}
            onTracking={abrirTrackingOrden}
            onRotulos={abrirRotulosOrden}
            onFinalizar={finalizarOrden}
            onAnular={anularOrden}
          />
          <OrdenesPanel
            tipo={tipo}
            ordenes={ordenesFinalizadas}
            paginacion={paginacionFinalizadas}
            titulo="Ordenes finalizadas"
            cargando={cargando}
            headerExtra={<FiltroFinalizadas filtro={filtroFinalizadas} onChange={(updater) => {
              setFiltroFinalizadas(updater);
              paginacionFinalizadas.resetPage();
            }} />}
            onDetalle={(orden) => setOrdenDetalle(orden)}
            onPdf={(orden) => generarOrdenPDF(tipo, orden)}
            onTracking={abrirTrackingOrden}
            onRotulos={abrirRotulosOrden}
            onFinalizar={finalizarOrden}
            onAnular={anularOrden}
          />
      </div>
      <OrdenModal
        tipo={tipoModalOrden || tipo}
        formulario={formularioPedido}
        clientes={clientes}
        productosTerminados={productosTerminados}
        productosProducidos={productosProducidos}
        productosCatalogo={productosCatalogo}
        proveedores={proveedores}
        productosVenta={productosVenta}
        preciosPedido={preciosPedidoAutomaticos}
        ordenesProduccion={ordenesProduccion}
        ingresosMercaderia={ingresosMercaderia}
        stocks={stocks}
        materialesProduccion={materialesProduccion}
        cargando={cargando}
        error={error}
        recipeAlert={alertaReceta}
        onError={setError}
        onRecipeAlert={setAlertaReceta}
        onClose={cerrarPedido}
        onSubmit={guardarPedido}
        setCampo={setCampoPedido}
        setItem={setItemPedido}
        agregarItem={agregarItemPedido}
        quitarItem={quitarItemPedido}
      />
      <OrdenDetalleModal
        tipo={tipo}
        orden={ordenDetalle}
        onClose={() => setOrdenDetalle(null)}
      />
      <OrdenTrackingModal
        tipo={tipo}
        orden={ordenTracking}
        qrDataUrl={ordenTrackingQr}
        onCopiar={copiarTrackingOrden}
        onClose={() => {
          setOrdenTracking(null);
          setOrdenTrackingQr("");
        }}
      />
      <OrdenRotulosModal
        tipo={tipo}
        orden={ordenRotulos}
        onImprimir={(salida, index) => generarRotuloOrdenPDF(tipo, ordenRotulos, salida, index)}
        onClose={() => setOrdenRotulos(null)}
      />
      <PedidoFinalizarModal
        pedido={pedidoAsignacion}
        stocks={stocks}
        cargando={cargando}
        onClose={() => setPedidoAsignacion(null)}
        onConfirm={confirmarFinalizacionPedido}
      />
    </div>
  );
}

function StockMateria({ items, paginacion, cargando, onVerLotes }) {
  return (
    <section className="panel stock-panel stock-panel-materia">
      <div className="stock-panel-head">
        <div>
          <h2>Materia prima/intermedio</h2>
        </div>
      </div>
      <div className="table-wrap">
        <table>
          <colgroup>
            <col style={{ width: "62%" }} />
            <col style={{ width: "38%" }} />
          </colgroup>
          <thead>
            <tr>
              <th>Producto</th>
              <th>Disponible</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan="2">{cargando ? "Cargando..." : "Sin materia prima."}</td></tr>
            ) : (
              items.map((item) => (
                <tr className="clickable-row" key={item.producto} title="Ver lotes" onClick={() => onVerLotes?.(item)}>
                  <td><strong>{item.producto}</strong></td>
                  <td>{item.kilosDisponibles} {item.unidadMedida || "kg"}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <Pagination page={paginacion.page} pageSize={paginacion.pageSize} totalItems={paginacion.totalItems} totalPages={paginacion.totalPages} onPageChange={paginacion.setPage} onPageSizeChange={paginacion.setPageSize} />
    </section>
  );
}

function StockTerminado({ items, paginacion, onCrearProduccion, onVerLotes }) {
  return (
    <section className="panel stock-panel stock-panel-final">
      <div className="stock-panel-head">
        <div>
          <h2>Producto final</h2>
        </div>
        <button className="btn btn-primary stock-primary-action" type="button" onClick={onCrearProduccion}>
          <Plus size={20} />
          Orden de produccion
        </button>
      </div>
      <div className="table-wrap">
        <table>
          <colgroup>
            <col style={{ width: "36%" }} />
            <col style={{ width: "22%" }} />
            <col style={{ width: "20%" }} />
            <col style={{ width: "22%" }} />
          </colgroup>
          <thead>
            <tr>
              <th>Producto</th>
              <th>Paquetes</th>
              <th>Parciales</th>
              <th>Disponible</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan="4">Sin productos terminados.</td></tr>
            ) : (
              items.map((item) => (
                <tr className="clickable-row" key={item._id} title="Ver lotes" onClick={() => onVerLotes?.(item)}>
                  <td><strong>{item.producto}</strong></td>
                  <td>{item.paquetesEnteros} x {etiquetaUnidad(item.cantidadPorPaquete, item.unidadMedida || "kg")}</td>
                  <td>
                    {item.paquetesParciales?.length
                      ? item.paquetesParciales.map((p) => etiquetaUnidad(p.kilosDisponibles, p.unidadMedida || item.unidadMedida || "kg")).join(" / ")
                      : "-"}
                  </td>
                  <td>{etiquetaUnidad(item.kilosDisponibles, item.unidadMedida || "kg")}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <Pagination page={paginacion.page} pageSize={paginacion.pageSize} totalItems={paginacion.totalItems} totalPages={paginacion.totalPages} onPageChange={paginacion.setPage} onPageSizeChange={paginacion.setPageSize} />
    </section>
  );
}

function StockFraccionado({ items, paginacion, onCrearFraccionado, onVerLotes }) {
  return (
    <section className="panel stock-panel stock-panel-fraccionado">
      <div className="stock-panel-head">
        <div>
          <h2>Producto fraccionado</h2>
        </div>
        <button className="btn btn-primary stock-primary-action" type="button" onClick={onCrearFraccionado}>
          <Plus size={20} />
          Orden de fraccionamiento
        </button>
      </div>
      <div className="table-wrap">
        <table>
          <colgroup>
            <col style={{ width: "38%" }} />
            <col style={{ width: "22%" }} />
            <col style={{ width: "18%" }} />
            <col style={{ width: "22%" }} />
          </colgroup>
          <thead>
            <tr>
              <th>Producto</th>
              <th>Presentacion</th>
              <th>Paquetes</th>
              <th>Kg</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan="4">Sin productos fraccionados.</td></tr>
            ) : (
              items.map((item) => (
                <tr className="clickable-row" key={item._id} title="Ver lotes" onClick={() => onVerLotes?.(item)}>
                  <td><strong>{item.producto}</strong></td>
                  <td>{formatPresentacion(item.presentacionGramos)}</td>
                  <td>{item.paquetes}</td>
                  <td>{item.kilosDisponibles} kg</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <Pagination page={paginacion.page} pageSize={paginacion.pageSize} totalItems={paginacion.totalItems} totalPages={paginacion.totalPages} onPageChange={paginacion.setPage} onPageSizeChange={paginacion.setPageSize} />
    </section>
  );
}

function StockLotesModal({ data, onClose }) {
  const item = data?.item || {};
  const [lotes, setLotes] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState("");
  const paginacion = useServerPagination();

  useEffect(() => {
    if (!data || !item.codigoStock) return undefined;
    let vigente = true;
    const cargarLotes = async () => {
      setCargando(true);
      setError("");
      try {
        const { data: respuesta } = await http.get("/operaciones/stocks/lotes", {
          params: {
            tipo: item.tipoStock,
            codigoStock: item.codigoStock,
            presentacion: item.presentacion ?? item.presentacionGramos ?? 0,
            proveedorId: data.proveedorId || undefined,
            page: paginacion.page,
            limit: paginacion.pageSize,
          },
        });
        if (!vigente) return;
        setLotes(Array.isArray(respuesta?.items) ? respuesta.items : []);
        paginacion.applyResponse(respuesta);
      } catch (err) {
        if (vigente) setError(err.response?.data?.error || "No se pudieron cargar los lotes.");
      } finally {
        if (vigente) setCargando(false);
      }
    };
    cargarLotes();
    return () => { vigente = false; };
  }, [
    data?.proveedorId,
    item.codigoStock,
    item.presentacion,
    item.presentacionGramos,
    item.tipoStock,
    paginacion.page,
    paginacion.pageSize,
  ]);
  if (!data) return null;

  const producto = item.producto || "Producto";

  return (
    <Modal
      abierto
      titulo={`Lotes de ${producto}`}
      subtitulo={data.tipo}
      size="wide"
      onClose={onClose}
      footer={<button className="btn" type="button" onClick={onClose}>Cerrar</button>}
    >
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Lote</th>
              <th>Origen</th>
              <th>Cant.</th>
              <th>Ubicacion</th>
              <th>Cant. disponible</th>
            </tr>
          </thead>
          <tbody>
            {lotes.length === 0 ? (
              <tr><td colSpan="5">{cargando ? "Cargando..." : error || "Sin lotes disponibles."}</td></tr>
            ) : (
              lotes.map((lote, index) => (
                <tr key={lote.id || `${lote.codigo}-${index}`}>
                  <td><strong>{lote.lote || lote.codigo || "-"}</strong></td>
                  <td>{lote.origen || "-"}</td>
                  <td>{numero(lote.cantidad).toLocaleString("es-AR")} {lote.unidad || ""}</td>
                  <td>{lote.ubicacion || "-"}</td>
                  <td>{numero(lote.disponible).toLocaleString("es-AR")} {lote.unidad || ""}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <Pagination page={paginacion.page} pageSize={paginacion.pageSize} totalItems={paginacion.totalItems} totalPages={paginacion.totalPages} onPageChange={paginacion.setPage} onPageSizeChange={paginacion.setPageSize} />
    </Modal>
  );
}

const pedidoItemKey = (item, index) => String(item._id || index);
const asignacionPedidoVacia = () => ({ ordenProduccionId: "", ordenFraccionadoId: "", cantidadPaquetes: "" });

function PedidoFinalizarModal({ pedido, stocks, cargando, onClose, onConfirm }) {
  const [asignaciones, setAsignaciones] = useState([]);
  const [errorLocal, setErrorLocal] = useState("");

  useEffect(() => {
    if (!pedido) {
      setAsignaciones([]);
      setErrorLocal("");
      return;
    }
    setAsignaciones((pedido.items || []).map((item, index) => ({
      itemId: item._id || "",
      itemIndex: index,
      producto: item.producto,
      tipoMedida: item.tipoMedida || "Peso",
      kilosPorPaquete: item.kilosPorPaquete,
      unidadMedida: item.unidadMedida || "kg",
      cantidadPaquetes: item.cantidadPaquetes,
      asignaciones: [{ ...asignacionPedidoVacia(), cantidadPaquetes: item.cantidadPaquetes || "" }],
    })));
    setErrorLocal("");
  }, [pedido]);

  if (!pedido) return null;

  const stockTerminadoParaItem = (item) =>
    (stocks?.terminados || []).find((stock) => normalizarProducto(stock.producto) === normalizarProducto(item.producto));

  const esPaqueteTerminado = (item) => {
    const stock = stockTerminadoParaItem(item);
    return Boolean(
      stock &&
      (stock.unidadMedida || "kg") === (item.unidadMedida || "kg") &&
      numero(stock.cantidadPorPaquete) === numero(item.kilosPorPaquete)
    );
  };

  const opcionesParaItem = (item) => {
    const opcionesFraccionado = (() => {
      const presentacionGramos = presentacionPedidoGramos(item);
      return (stocks?.fraccionados || [])
        .filter((stock) =>
          normalizarProducto(stock.producto) === normalizarProducto(item.producto) &&
          Number(stock.presentacionGramos || 0) === Number(presentacionGramos)
        )
        .flatMap((stock) => stock.lotes || [])
        .filter((lote) => numero(lote.disponible) > 0 && lote.ordenFraccionadoId)
        .map((lote) => ({
          value: `fraccionado:${lote.ordenFraccionadoId}`,
          label: `FRAC ${lote.codigo || "-"} - ${numero(lote.disponible).toLocaleString("es-AR")} paquetes disponibles`,
          disponible: numero(lote.disponible),
        }));
    })();
    if (esPaqueteTerminado(item)) {
      const opcionesProduccion = (stockTerminadoParaItem(item)?.lotes || [])
        .filter((lote) => numero(lote.paquetesDisponibles) > 0 && lote.id)
        .map((lote) => ({
          value: `produccion:${lote.id}`,
          label: `PROD ${lote.codigo || "-"} - ${numero(lote.paquetesDisponibles).toLocaleString("es-AR")} paquetes disponibles`,
          disponible: numero(lote.paquetesDisponibles),
        }));
      return [...opcionesProduccion, ...opcionesFraccionado];
    }
    return opcionesFraccionado;
  };

  const valorOrigen = (asignacion) => asignacion.ordenProduccionId
    ? `produccion:${asignacion.ordenProduccionId}`
    : asignacion.ordenFraccionadoId
      ? `fraccionado:${asignacion.ordenFraccionadoId}`
      : "";

  const actualizarOrigen = (itemIndex, asignacionIndex, value) => {
    const [tipoOrigen, ordenId = ""] = String(value || "").split(":");
    setAsignaciones((prev) => prev.map((item, index) => index !== itemIndex ? item : {
      ...item,
      asignaciones: item.asignaciones.map((asignacion, rowIndex) => rowIndex !== asignacionIndex ? asignacion : {
        ...asignacion,
        ordenProduccionId: tipoOrigen === "produccion" ? ordenId : "",
        ordenFraccionadoId: tipoOrigen === "fraccionado" ? ordenId : "",
      }),
    }));
  };

  const actualizarAsignacion = (itemIndex, asignacionIndex, campo, value) => {
    setAsignaciones((prev) => prev.map((item, index) => index !== itemIndex ? item : {
      ...item,
      asignaciones: item.asignaciones.map((asignacion, rowIndex) =>
        rowIndex === asignacionIndex ? { ...asignacion, [campo]: value } : asignacion
      ),
    }));
  };

  const agregarAsignacion = (itemIndex) => {
    setAsignaciones((prev) => prev.map((item, index) => index === itemIndex
      ? { ...item, asignaciones: [...item.asignaciones, asignacionPedidoVacia()] }
      : item));
  };

  const quitarAsignacion = (itemIndex, asignacionIndex) => {
    setAsignaciones((prev) => prev.map((item, index) => {
      if (index !== itemIndex) return item;
      return {
        ...item,
        asignaciones: item.asignaciones.length > 1
          ? item.asignaciones.filter((_, rowIndex) => rowIndex !== asignacionIndex)
          : [asignacionPedidoVacia()],
      };
    }));
  };

  const validarYConfirmar = async (event) => {
    event.preventDefault();
    for (const item of asignaciones) {
      const opciones = opcionesParaItem(item);
      const total = item.asignaciones.reduce((sum, asignacion) => sum + numero(asignacion.cantidadPaquetes), 0);
      if (total !== numero(item.cantidadPaquetes)) {
        setErrorLocal(`${item.producto}: las asignaciones deben sumar ${item.cantidadPaquetes} paquetes.`);
        return;
      }
      if (item.asignaciones.some((asignacion) => !valorOrigen(asignacion) || numero(asignacion.cantidadPaquetes) <= 0)) {
        setErrorLocal(`${item.producto}: completa la orden de origen y la cantidad.`);
        return;
      }
      const consumos = new Map();
      item.asignaciones.forEach((asignacion) => {
        const origen = valorOrigen(asignacion);
        consumos.set(origen, numero(consumos.get(origen)) + numero(asignacion.cantidadPaquetes));
      });
      for (const [ordenId, cantidad] of consumos.entries()) {
        const disponible = opciones.find((opcion) => String(opcion.value) === String(ordenId))?.disponible || 0;
        if (cantidad > disponible) {
          setErrorLocal(`${item.producto}: la orden seleccionada tiene ${disponible} paquetes disponibles.`);
          return;
        }
      }
    }
    setErrorLocal("");
    try {
      await onConfirm(pedido, asignaciones);
    } catch (err) {
      setErrorLocal(err.message || "No se pudo finalizar la orden de pedido.");
    }
  };

  return (
    <Modal
      abierto
      titulo="Finalizar orden de pedido"
      subtitulo={pedido.codigo || ""}
      size="wide"
      onClose={onClose}
      footer={
        <>
          <button className="btn" type="button" onClick={onClose}>Cancelar</button>
          <button className="btn btn-primary" form="pedido-trazabilidad-form" type="submit" disabled={cargando}>Finalizar pedido</button>
        </>
      }
    >
      {errorLocal && <div className="alert alert-error">{errorLocal}</div>}
      <form id="pedido-trazabilidad-form" onSubmit={validarYConfirmar}>
        <div className="trace-list">
          {asignaciones.map((item, itemIndex) => {
            const desdeProduccion = esPaqueteTerminado(item);
            const tipoOrden = desdeProduccion ? "produccion o fraccionamiento" : "fraccionamiento";
            const opciones = opcionesParaItem(item);
            const totalAsignado = item.asignaciones.reduce((sum, asignacion) => sum + numero(asignacion.cantidadPaquetes), 0);
            return (
              <section className="form-section" key={pedidoItemKey(item, itemIndex)}>
                <div className="section-title-actions">
                  <h3>{item.producto}</h3>
                  <button className="btn" type="button" onClick={() => agregarAsignacion(itemIndex)}><Plus size={17} />Agregar origen</button>
                </div>
                <p className="trace-help">
                  Requerido: {numero(item.cantidadPaquetes).toLocaleString("es-AR")} paquetes de {formatPresentacion(presentacionPedidoGramos(item))}. Asignado: {totalAsignado.toLocaleString("es-AR")}.
                </p>
                {opciones.length === 0 && <div className="alert alert-error">No hay ordenes de {tipoOrden} finalizadas con saldo para esta presentacion.</div>}
                <div className="ops-line-list">
                  {item.asignaciones.map((asignacion, asignacionIndex) => (
                    <div className="ops-line order-assignment-line" key={asignacionIndex}>
                      <SearchableSelect
                        label={`Orden de ${tipoOrden}`}
                        value={valorOrigen(asignacion)}
                        onChange={(value) => actualizarOrigen(itemIndex, asignacionIndex, value)}
                        options={opciones}
                      />
                      <Field label="Cant paquetes" type="text" inputMode="decimal" step="1" min="1" value={asignacion.cantidadPaquetes} onChange={(event) => actualizarAsignacion(itemIndex, asignacionIndex, "cantidadPaquetes", event.target.value)} />
                      <button className="btn btn-danger product-remove" type="button" onClick={() => quitarAsignacion(itemIndex, asignacionIndex)}><XCircle size={16} /></button>
                    </div>
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      </form>
    </Modal>
  );
}

const analizarItemPedido = (item, stocks, ordenesProduccion = [], ordenesFraccionado = []) => {
  const productoKey = normalizarProducto(item.producto);
  const presentacionGramos = presentacionPedidoGramos(item);
  const paquetesNecesarios = numero(item.cantidadPaquetes);
  const cantidadPorPaquete = numero(item.kilosPorPaquete);
  const unidadItem = item.unidadMedida || "kg";
  const fraccionado = (stocks.fraccionados || []).find(
    (stock) =>
      normalizarProducto(stock.producto) === productoKey &&
      numero(stock.presentacionGramos) === presentacionGramos,
  );
  const paquetesDisponibles = numero(fraccionado?.paquetes);
  const terminado = (stocks.terminados || []).find((stock) => normalizarProducto(stock.producto) === productoKey);
  const kilosTerminados = numero(terminado?.kilosDisponibles);
  const paquetesTerminadosDisponibles = numero(terminado?.paquetesEnteros);
  const unidadTerminado = terminado?.unidadMedida || unidadItem;
  const cantidadPaqueteTerminado = numero(terminado?.cantidadPorPaquete || cantidadPorPaquete);
  const esPaqueteTerminado = unidadItem === unidadTerminado && cantidadPorPaquete === cantidadPaqueteTerminado;
  const produccionPendiente = ordenesProduccion.find(
    (orden) => orden.estado === "pendiente" && normalizarProducto(orden.productoTerminado) === productoKey,
  );
  const fraccionadoPendiente = ordenesFraccionado.find(
    (orden) =>
      orden.estado === "pendiente" &&
      normalizarProducto(orden.producto) === productoKey &&
      (orden.salidas || []).some((salida) => numero(salida.presentacionGramos) === presentacionGramos),
  );
  const avisoProduccionPendiente = () => ({
    disponible: false,
    observacion: `La orden ${produccionPendiente.codigo} esta pendiente. Finalizala para incorporar el producto al stock.`,
  });
  const avisoFraccionadoPendiente = () => ({
    disponible: false,
    observacion: `La orden ${fraccionadoPendiente.codigo} esta pendiente. Finalizala para incorporar los paquetes al stock fraccionado.`,
  });

  if (esPaqueteTerminado) {
    if (paquetesTerminadosDisponibles >= paquetesNecesarios && paquetesNecesarios > 0) {
      return {
        disponible: true,
        observacion: `Stock terminado disponible: ${paquetesTerminadosDisponibles.toLocaleString("es-AR")} paquetes de ${etiquetaUnidad(cantidadPaqueteTerminado, unidadTerminado)}.`,
      };
    }

    const faltanPaquetes = Math.max(paquetesNecesarios - paquetesTerminadosDisponibles, 0);
    if (produccionPendiente) return avisoProduccionPendiente();
    return {
      disponible: false,
      observacion: `Stock terminado insuficiente: hay ${paquetesTerminadosDisponibles.toLocaleString("es-AR")} y faltan ${faltanPaquetes.toLocaleString("es-AR")}. Es necesario producirlo.`,
      accion: {
        tipo: "produccion",
        producto: item.producto,
        cantidadPaquetes25kg: faltanPaquetes,
      },
    };
  }

  if (item.tipoMedida === "Volumen") {
    if (produccionPendiente) return avisoProduccionPendiente();
    return {
      disponible: false,
      observacion: `No hay paquetes terminados de ${etiquetaUnidad(cantidadPorPaquete, unidadItem)}. Es necesario producirlo.`,
      accion: {
        tipo: "produccion",
        producto: item.producto,
        cantidadPaquetes25kg: paquetesNecesarios,
      },
    };
  }

  if (paquetesDisponibles >= paquetesNecesarios && paquetesNecesarios > 0) {
    return {
      disponible: true,
      observacion: `Stock fraccionado disponible: ${paquetesDisponibles.toLocaleString("es-AR")} paquetes.`,
    };
  }

  const faltanPaquetes = Math.max(paquetesNecesarios - paquetesDisponibles, 0);
  const kilosFaltantes = faltanPaquetes * convertirUnidad(cantidadPorPaquete, unidadItem, "kg");
  const base = paquetesDisponibles > 0
    ? `Stock fraccionado insuficiente: hay ${paquetesDisponibles.toLocaleString("es-AR")} y faltan ${faltanPaquetes.toLocaleString("es-AR")}. `
    : "";

  if (fraccionadoPendiente) return avisoFraccionadoPendiente();

  if (kilosTerminados >= kilosFaltantes && kilosFaltantes > 0) {
    return {
      disponible: false,
      observacion: `${base}Existe producto terminado para fraccionar.`,
      accion: {
        tipo: "fraccionado",
        producto: item.producto,
        presentacionGramos,
        cantidadPaquetes: faltanPaquetes,
        kilosAFraccionar: kilosFaltantes,
      },
    };
  }

  if (kilosTerminados > 0) {
    if (produccionPendiente) return avisoProduccionPendiente();
    return {
      disponible: false,
      observacion: `${base}Hay ${kilosTerminados.toLocaleString("es-AR")} ${unidadTerminado} terminados; es necesario producir el faltante.`,
      accion: {
        tipo: "produccion",
        producto: item.producto,
        cantidadPaquetes25kg: Math.max(Math.ceil(Math.max(kilosFaltantes - kilosTerminados, 0) / (cantidadPaqueteTerminado || 25)), 1),
      },
    };
  }

  if (produccionPendiente) return avisoProduccionPendiente();
  return {
    disponible: false,
    observacion: `${base}Es necesario producirlo.`,
    accion: {
      tipo: "produccion",
      producto: item.producto,
      cantidadPaquetes25kg: Math.max(Math.ceil(kilosFaltantes / (cantidadPaqueteTerminado || 25)), 1),
    },
  };
};

const pedidoPuedeFinalizarse = (pedido, stocks) => {
  const necesidades = new Map();
  for (const item of pedido.items || []) {
    const key = `${normalizarProducto(item.producto)}|${item.unidadMedida || "kg"}|${numero(item.kilosPorPaquete)}`;
    necesidades.set(key, (necesidades.get(key) || 0) + numero(item.cantidadPaquetes));
  }

  for (const [key, paquetesNecesarios] of necesidades.entries()) {
    const [productoKey, unidad, cantidadText] = key.split("|");
    const terminado = (stocks.terminados || []).find((item) => normalizarProducto(item.producto) === productoKey);
    if (terminado?.unidadMedida === unidad && numero(terminado?.cantidadPorPaquete) === Number(cantidadText)) {
      if (numero(terminado?.paquetesEnteros) < paquetesNecesarios) return false;
      continue;
    }
    if (unidad === "ml" || unidad === "l") return false;
    const gramosText = Math.round(convertirUnidad(Number(cantidadText), unidad, "kg") * 1000);
    const stock = (stocks.fraccionados || []).find(
      (item) => normalizarProducto(item.producto) === productoKey && numero(item.presentacionGramos) === Number(gramosText),
    );
    if (numero(stock?.paquetes) < paquetesNecesarios) return false;
  }

  return necesidades.size > 0;
};

function PedidosPendientesModal({ abierto, stocks, ordenesProduccion, ordenesFraccionado, cargando, onFinalizarPedido, onRealizarAccion, onClose }) {
  const [pedidos, setPedidos] = useState([]);
  const [cargandoPedidos, setCargandoPedidos] = useState(false);
  const [errorPedidos, setErrorPedidos] = useState("");
  const paginacion = useServerPagination();

  useEffect(() => {
    if (!abierto) return undefined;
    let vigente = true;
    const cargarPedidos = async () => {
      setCargandoPedidos(true);
      setErrorPedidos("");
      try {
        const { data } = await http.get("/operaciones/ordenes/pedidos", {
          params: {
            estado: "pendiente",
            page: paginacion.page,
            limit: paginacion.pageSize,
          },
        });
        if (!vigente) return;
        setPedidos(Array.isArray(data?.items) ? data.items : []);
        paginacion.applyResponse(data);
      } catch (err) {
        if (vigente) setErrorPedidos(err.response?.data?.error || "No se pudieron cargar los pedidos.");
      } finally {
        if (vigente) setCargandoPedidos(false);
      }
    };
    cargarPedidos();
    return () => { vigente = false; };
  }, [abierto, paginacion.page, paginacion.pageSize]);
  return (
    <Modal
      abierto={abierto}
      titulo="Pedidos pendientes"
      subtitulo="Guia para decidir si hace falta fraccionar o producir antes de crear nuevas ordenes."
      size="pending-orders"
      onClose={onClose}
      footer={<button className="btn" type="button" onClick={onClose}>Cerrar</button>}
    >
      <div className="pending-orders-guide">
        {pedidos.length === 0 ? (
          <div className="empty-state">{cargando || cargandoPedidos ? "Cargando pedidos..." : errorPedidos || "No hay pedidos pendientes."}</div>
        ) : (
          pedidos.map((pedido) => (
            <section className="pending-order-card" key={pedido._id}>
              <div className="pending-order-head">
                <div>
                  <h3>{pedido.codigo || "Pedido sin codigo"}</h3>
                  <p>{pedido.clienteNombre || "Cliente sin asignar"} - {formatDate(pedido.fecha)}</p>
                </div>
                <div className="pending-order-actions">
                  <span className="badge warn">pendiente</span>
                  {pedidoPuedeFinalizarse(pedido, stocks) && (
                    <button className="btn btn-primary" type="button" onClick={() => onFinalizarPedido(pedido)}>
                      <CheckCircle2 size={16} />
                      Finalizar pedido
                    </button>
                  )}
                </div>
              </div>
              <div className="table-wrap pending-orders-table">
                <table>
                  <thead>
                    <tr>
                      <th>Producto</th>
                      <th>Presentacion</th>
                      <th>Paquetes pedidos</th>
                      <th>Cantidad total</th>
                      <th>Stock</th>
                      <th>Observacion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(pedido.items || []).map((item, index) => {
                      const analisis = analizarItemPedido(item, stocks, ordenesProduccion, ordenesFraccionado);
                      return (
                        <tr key={`${pedido._id}-${index}`}>
                          <td><strong>{item.producto}</strong></td>
                          <td>{presentacionPedidoTexto(item)}</td>
                          <td>{numero(item.cantidadPaquetes).toLocaleString("es-AR")}</td>
                          <td>{kgPedido(item).toLocaleString("es-AR")} {item.unidadMedida || "kg"}</td>
                          <td>
                            <span className={`stock-check ${analisis.disponible ? "ok" : "bad"}`}>
                              {analisis.disponible ? <CheckCircle2 size={17} /> : <XCircle size={17} />}
                              {analisis.disponible ? "Disponible" : "No disponible"}
                            </span>
                          </td>
                          <td>
                            <div className="pending-action-cell">
                              <span>{analisis.observacion}</span>
                              {!analisis.disponible && analisis.accion && (
                                <button className="btn btn-primary" type="button" onClick={() => onRealizarAccion(analisis.accion)}>
                                  Realizar
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>
          ))
        )}
        <Pagination page={paginacion.page} pageSize={paginacion.pageSize} totalItems={paginacion.totalItems} totalPages={paginacion.totalPages} onPageChange={paginacion.setPage} onPageSizeChange={paginacion.setPageSize} />
      </div>
    </Modal>
  );
}

const calcularRecetasPedido = (pedido, productosCatalogo, proveedores, selecciones = {}) => {
  if (!pedido) return { productos: [], totalVenta: 0 };
  const productosPorId = new Map(
    (productosCatalogo || []).map((producto) => [String(producto._id), producto]),
  );
  const productosPorNombre = new Map(
    (productosCatalogo || []).map((producto) => [normalizarProducto(producto.nombre), producto]),
  );
  const pedidosAgrupados = new Map();

  for (const item of pedido.items || []) {
    const producto = productosPorId.get(String(item.productoId || "")) ||
      productosPorNombre.get(normalizarProducto(item.producto));
    const key = String(producto?._id || normalizarProducto(item.producto));
    const rendimiento = producto?.recetaRendimiento || {};
    const cantidadItem = numero(item.kilosTotales || numero(item.kilosPorPaquete) * numero(item.cantidadPaquetes));
    const cantidadRendimiento = producto
      ? convertirUnidad(cantidadItem, item.unidadMedida || producto.unidadMedida, rendimiento.unidadMedida || producto.unidadMedida)
      : cantidadItem;
    const actual = pedidosAgrupados.get(key) || {
      producto,
      nombre: producto?.nombre || item.producto,
      cantidad: 0,
      unidad: rendimiento.unidadMedida || producto?.unidadMedida || item.unidadMedida || "kg",
    };
    actual.cantidad += cantidadRendimiento;
    pedidosAgrupados.set(key, actual);
  }

  const resultados = [...pedidosAgrupados.values()].map((pedidoProducto) => {
    const producto = pedidoProducto.producto;
    const componentes = new Map();
    const errores = new Set();
    const productoKey = String(producto?._id || normalizarProducto(pedidoProducto.nombre));
    const rendimiento = producto?.recetaRendimiento || {};
    const agregar = ({ tipo, id, nombre, cantidad, unidad }) => {
      const key = `${tipo}:${id || normalizarProducto(nombre)}`;
      const existente = componentes.get(key);
      if (existente) existente.cantidad += cantidad;
      else componentes.set(key, { key, tipo, id: String(id || ""), nombre, cantidad, unidad });
    };
    const expandir = (productoActual, cantidadRecetas, recorrido = []) => {
      const actualId = String(productoActual?._id || normalizarProducto(productoActual?.nombre));
      if (recorrido.includes(actualId)) {
        errores.add(`Referencia circular en la receta de ${productoActual?.nombre || "producto"}.`);
        return;
      }
      for (const material of productoActual?.materialesProduccion || []) {
        agregar({ tipo: "material", id: idReferencia(material.materialId), nombre: material.nombre,
          cantidad: numero(material.cantidad) * cantidadRecetas, unidad: "unidad" });
      }
      for (const ingrediente of productoActual?.receta || []) {
        const cantidadIngrediente = numero(ingrediente.cantidad) * cantidadRecetas;
        if (ingrediente.tipo === "Materia prima") {
          const unidad = ingrediente.tipoMedida === "Volumen" ? "l" : "kg";
          agregar({ tipo: "ingrediente", id: idReferencia(ingrediente.productoId), nombre: ingrediente.producto,
            cantidad: convertirUnidad(cantidadIngrediente, ingrediente.unidadMedida, unidad), unidad });
          continue;
        }
        const intermedio = productosPorId.get(idReferencia(ingrediente.productoId)) ||
          productosPorNombre.get(normalizarProducto(ingrediente.producto));
        const rendimientoIntermedio = intermedio?.recetaRendimiento || {};
        if (!intermedio || numero(rendimientoIntermedio.cantidad) <= 0) {
          errores.add(`No se pudo calcular el intermedio ${ingrediente.producto}.`);
          continue;
        }
        const cantidadIntermedia = convertirUnidad(
          cantidadIngrediente,
          ingrediente.unidadMedida,
          rendimientoIntermedio.unidadMedida,
        );
        expandir(intermedio, cantidadIntermedia / numero(rendimientoIntermedio.cantidad), [...recorrido, actualId]);
      }
    };
    if (!producto || numero(rendimiento.cantidad) <= 0) errores.add(`El producto ${pedidoProducto.nombre} no tiene una receta valida.`);
    else expandir(producto, pedidoProducto.cantidad / numero(rendimiento.cantidad));

    const lineas = [...componentes.values()].map((componente) => {
      const opciones = (proveedores || []).map((proveedor) => {
        const asociado = componente.tipo === "material"
          ? (proveedor.materiales || []).find((material) => material.activo !== false &&
              ((componente.id && idReferencia(material.materialProduccionId) === componente.id) ||
                normalizarProducto(material.nombre) === normalizarProducto(componente.nombre)))
          : (proveedor.productos || []).find((item) => item.activo !== false &&
              ((componente.id && idProductoProveedor(item) === componente.id) ||
                normalizarProducto(item.nombre) === normalizarProducto(componente.nombre)));
        return asociado ? { proveedor, precio: numero(asociado.precioCompraPorUnidad ?? asociado.precioPorKg) } : null;
      }).filter(Boolean).sort((a, b) => {
        if (a.precio <= 0 && b.precio > 0) return 1;
        if (b.precio <= 0 && a.precio > 0) return -1;
        return a.precio - b.precio || a.proveedor.razonSocial.localeCompare(b.proveedor.razonSocial);
      });
      const seleccionKey = `${productoKey}:${componente.key}`;
      const seleccionada = opciones.find(
        (opcion) => String(opcion.proveedor._id) === String(selecciones[seleccionKey]),
      ) || opciones[0] || null;
      return { ...componente, seleccionKey, opciones, seleccionada,
        costoTotal: componente.cantidad * numero(seleccionada?.precio) };
    });
    const costoIngredientes = lineas.filter((linea) => linea.tipo === "ingrediente")
      .reduce((total, linea) => total + linea.costoTotal, 0);
    const costoMateriales = lineas.filter((linea) => linea.tipo === "material")
      .reduce((total, linea) => total + linea.costoTotal, 0);
    const costoTotal = costoIngredientes + costoMateriales;
    const margen = Math.max(numero(producto?.margenGananciaPorcentaje), 0);
    const precioVenta = costoTotal * (1 + margen / 100);
    return { ...pedidoProducto, productoKey, errores: [...errores], lineas, costoIngredientes,
      costoMateriales, costoTotal, margen, importeMargen: precioVenta - costoTotal, precioVenta,
      faltantes: lineas.filter((linea) => !linea.seleccionada || linea.seleccionada.precio <= 0) };
  });
  return { productos: resultados, totalVenta: resultados.reduce((total, producto) => total + producto.precioVenta, 0) };
};

const preciosDeSimulacion = (simulacion) => simulacion.productos.map((producto) => {
  const unidadPrecio = producto.producto?.tipoMedida === "Volumen" ? "l" : "kg";
  const cantidadUnidadPrecio = convertirUnidad(producto.cantidad, producto.unidad, unidadPrecio);
  return {
    productoId: producto.producto?._id || "",
    nombre: producto.nombre,
    precioPorUnidad: cantidadUnidadPrecio > 0 ? producto.precioVenta / cantidadUnidadPrecio : 0,
    precioTotal: producto.precioVenta,
    faltantes: producto.faltantes,
    errores: producto.errores,
  };
});

function PedidoCostosModal({ pedido, productosCatalogo, proveedores, onAplicar, onClose }) {
  const [selecciones, setSelecciones] = useState({});

  useEffect(() => {
    setSelecciones({});
  }, [pedido?._id, pedido?._simulacionKey]);

  const simulacion = useMemo(() => {
    if (!pedido) return { productos: [], totalVenta: 0 };
    const productosPorId = new Map(
      (productosCatalogo || []).map((producto) => [String(producto._id), producto]),
    );
    const productosPorNombre = new Map(
      (productosCatalogo || []).map((producto) => [normalizarProducto(producto.nombre), producto]),
    );
    const pedidosAgrupados = new Map();

    for (const item of pedido.items || []) {
      const producto =
        productosPorId.get(String(item.productoId || "")) ||
        productosPorNombre.get(normalizarProducto(item.producto));
      const key = String(producto?._id || normalizarProducto(item.producto));
      const rendimiento = producto?.recetaRendimiento || {};
      const cantidadItem = numero(item.kilosTotales || numero(item.kilosPorPaquete) * numero(item.cantidadPaquetes));
      const cantidadRendimiento = producto
        ? convertirUnidad(cantidadItem, item.unidadMedida || producto.unidadMedida, rendimiento.unidadMedida || producto.unidadMedida)
        : cantidadItem;
      const actual = pedidosAgrupados.get(key) || {
        producto,
        nombre: producto?.nombre || item.producto,
        cantidad: 0,
        unidad: rendimiento.unidadMedida || producto?.unidadMedida || item.unidadMedida || "kg",
      };
      actual.cantidad += cantidadRendimiento;
      pedidosAgrupados.set(key, actual);
    }

    const resultados = [...pedidosAgrupados.values()].map((pedidoProducto) => {
      const producto = pedidoProducto.producto;
      const componentes = new Map();
      const errores = new Set();
      const productoKey = String(producto?._id || normalizarProducto(pedidoProducto.nombre));
      const rendimiento = producto?.recetaRendimiento || {};

      const agregar = ({ tipo, id, nombre, cantidad, unidad }) => {
        const key = `${tipo}:${id || normalizarProducto(nombre)}`;
        const existente = componentes.get(key);
        if (existente) {
          existente.cantidad += cantidad;
        } else {
          componentes.set(key, { key, tipo, id: String(id || ""), nombre, cantidad, unidad });
        }
      };

      const expandir = (productoActual, cantidadRecetas, recorrido = []) => {
        const actualId = String(productoActual?._id || normalizarProducto(productoActual?.nombre));
        if (recorrido.includes(actualId)) {
          errores.add(`Referencia circular en la receta de ${productoActual?.nombre || "producto"}.`);
          return;
        }

        for (const material of productoActual?.materialesProduccion || []) {
          agregar({
            tipo: "material",
            id: idReferencia(material.materialId),
            nombre: material.nombre,
            cantidad: numero(material.cantidad) * cantidadRecetas,
            unidad: "unidad",
          });
        }

        for (const ingrediente of productoActual?.receta || []) {
          const cantidadIngrediente = numero(ingrediente.cantidad) * cantidadRecetas;
          if (ingrediente.tipo === "Materia prima") {
            const unidad = ingrediente.tipoMedida === "Volumen" ? "l" : "kg";
            agregar({
              tipo: "ingrediente",
              id: idReferencia(ingrediente.productoId),
              nombre: ingrediente.producto,
              cantidad: convertirUnidad(cantidadIngrediente, ingrediente.unidadMedida, unidad),
              unidad,
            });
            continue;
          }

          const intermedio =
            productosPorId.get(idReferencia(ingrediente.productoId)) ||
            productosPorNombre.get(normalizarProducto(ingrediente.producto));
          const rendimientoIntermedio = intermedio?.recetaRendimiento || {};
          if (!intermedio || numero(rendimientoIntermedio.cantidad) <= 0) {
            errores.add(`No se pudo calcular el intermedio ${ingrediente.producto}.`);
            continue;
          }
          const cantidadIntermedia = convertirUnidad(
            cantidadIngrediente,
            ingrediente.unidadMedida,
            rendimientoIntermedio.unidadMedida,
          );
          expandir(
            intermedio,
            cantidadIntermedia / numero(rendimientoIntermedio.cantidad),
            [...recorrido, actualId],
          );
        }
      };

      if (!producto || numero(rendimiento.cantidad) <= 0) {
        errores.add(`El producto ${pedidoProducto.nombre} no tiene una receta valida.`);
      } else {
        expandir(producto, pedidoProducto.cantidad / numero(rendimiento.cantidad));
      }

      const lineas = [...componentes.values()].map((componente) => {
        const opciones = (proveedores || [])
          .map((proveedor) => {
            const asociado = componente.tipo === "material"
              ? (proveedor.materiales || []).find(
                  (material) =>
                    material.activo !== false &&
                    ((componente.id &&
                      idReferencia(material.materialProduccionId) === componente.id) ||
                      normalizarProducto(material.nombre) === normalizarProducto(componente.nombre)),
                )
              : (proveedor.productos || []).find(
                  (item) =>
                    item.activo !== false &&
                    ((componente.id && idProductoProveedor(item) === componente.id) ||
                      normalizarProducto(item.nombre) === normalizarProducto(componente.nombre)),
                );
            if (!asociado) return null;
            return {
              proveedor,
              precio: numero(asociado.precioCompraPorUnidad ?? asociado.precioPorKg),
            };
          })
          .filter(Boolean)
          .sort((a, b) => {
            if (a.precio <= 0 && b.precio > 0) return 1;
            if (b.precio <= 0 && a.precio > 0) return -1;
            return a.precio - b.precio || a.proveedor.razonSocial.localeCompare(b.proveedor.razonSocial);
          });
        const seleccionKey = `${productoKey}:${componente.key}`;
        const seleccionada =
          opciones.find(
            (opcion) => String(opcion.proveedor._id) === String(selecciones[seleccionKey]),
          ) ||
          opciones[0] ||
          null;
        return {
          ...componente,
          seleccionKey,
          opciones,
          seleccionada,
          costoTotal: componente.cantidad * numero(seleccionada?.precio),
        };
      });

      const costoIngredientes = lineas
        .filter((linea) => linea.tipo === "ingrediente")
        .reduce((total, linea) => total + linea.costoTotal, 0);
      const costoMateriales = lineas
        .filter((linea) => linea.tipo === "material")
        .reduce((total, linea) => total + linea.costoTotal, 0);
      const costoTotal = costoIngredientes + costoMateriales;
      const margen = Math.max(numero(producto?.margenGananciaPorcentaje), 0);
      const precioVenta = costoTotal * (1 + margen / 100);

      return {
        ...pedidoProducto,
        productoKey,
        errores: [...errores],
        lineas,
        costoIngredientes,
        costoMateriales,
        costoTotal,
        margen,
        importeMargen: precioVenta - costoTotal,
        precioVenta,
        faltantes: lineas.filter((linea) => !linea.seleccionada || linea.seleccionada.precio <= 0),
      };
    });

    return {
      productos: resultados,
      totalVenta: resultados.reduce((total, producto) => total + producto.precioVenta, 0),
    };
  }, [pedido, productosCatalogo, proveedores, selecciones]);

  const preciosAplicables = useMemo(() => preciosDeSimulacion(simulacion), [simulacion]);
  const puedeAplicar = preciosAplicables.length > 0 && preciosAplicables.every(
    (producto) =>
      producto.precioPorUnidad > 0 &&
      producto.faltantes.length === 0 &&
      producto.errores.length === 0,
  );
  const precioVentaGuardado = (producto) => {
    const precioUnidad = numero(producto.producto?.precioVentaPorUnidad);
    const unidadPrecio = producto.producto?.tipoMedida === "Volumen" ? "l" : "kg";
    const cantidad = convertirUnidad(producto.cantidad, producto.unidad, unidadPrecio);
    return precioUnidad > 0 && cantidad > 0 ? precioUnidad * cantidad : 0;
  };
  const totalVentaGuardado = simulacion.productos.reduce(
    (total, producto) => total + precioVentaGuardado(producto),
    0,
  );

  return (
    <Modal
      abierto={Boolean(pedido)}
      titulo={`Simulacion de recetas: ${pedido?.codigo || ""}`}
      subtitulo={pedido?.clienteNombre || ""}
      size="comparison"
      onClose={onClose}
      footer={
        <>
          <button className="btn" type="button" onClick={onClose}>Cerrar</button>
          {onAplicar && (
            <button
              className="btn btn-primary"
              type="button"
              disabled={!puedeAplicar}
              onClick={() => onAplicar(preciosAplicables)}
            >
              Aplicar precios al pedido
            </button>
          )}
        </>
      }
    >
      <div className="order-recipe-simulation">
        {simulacion.productos.map((producto) => (
          <section className="order-simulation-product" key={producto.productoKey}>
            <div className="panel-title-row">
              <div>
                <h3>{producto.nombre}</h3>
                <p>
                  Cantidad solicitada: {producto.cantidad.toLocaleString("es-AR")} {producto.unidad}
                </p>
              </div>
              <div className="order-simulation-prices">
                <span>Precio simulado</span>
                <strong className="order-simulation-price">{formatoPrecio(producto.precioVenta)}</strong>
                {precioVentaGuardado(producto) > 0 && (
                  <small>Precio de venta guardado: {formatoPrecio(precioVentaGuardado(producto))}</small>
                )}
              </div>
            </div>

            {producto.errores.map((errorProducto) => (
              <div className="alert alert-error" key={errorProducto}>{errorProducto}</div>
            ))}

            {producto.lineas.length > 0 && (
              <div className="table-wrap simulation-table">
                <table>
                  <thead>
                    <tr>
                      <th>Componente</th>
                      <th>Tipo</th>
                      <th>Cantidad</th>
                      <th>Proveedor</th>
                      <th>Costo unitario</th>
                      <th>Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {producto.lineas.map((linea) => (
                      <tr key={linea.seleccionKey}>
                        <td><strong>{linea.nombre}</strong></td>
                        <td>{linea.tipo === "material" ? "Material" : "Materia prima"}</td>
                        <td>{linea.cantidad.toLocaleString("es-AR", { maximumFractionDigits: 6 })} {linea.unidad}</td>
                        <td>
                          {linea.opciones.length ? (
                            <select
                              className="simulation-provider-select"
                              value={linea.seleccionada?.proveedor?._id || ""}
                              onChange={(event) =>
                                setSelecciones((actual) => ({
                                  ...actual,
                                  [linea.seleccionKey]: event.target.value,
                                }))
                              }
                            >
                              {linea.opciones.map((opcion) => (
                                <option key={opcion.proveedor._id} value={opcion.proveedor._id}>
                                  {opcion.proveedor.razonSocial}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <span className="missing-cost">Sin proveedor asociado</span>
                          )}
                        </td>
                        <td>{formatoPrecio(linea.seleccionada?.precio)} / {linea.unidad}</td>
                        <td><strong>{formatoPrecio(linea.costoTotal)}</strong></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {producto.faltantes.length > 0 && (
              <div className="simulation-warning">
                El cálculo es parcial: hay {producto.faltantes.length} componente(s) sin precio.
              </div>
            )}

            <div className="simulation-totals">
              <div><span>Ingredientes</span><strong>{formatoPrecio(producto.costoIngredientes)}</strong></div>
              <div><span>Materiales</span><strong>{formatoPrecio(producto.costoMateriales)}</strong></div>
              <div className="simulation-grand-total"><span>Costo total</span><strong>{formatoPrecio(producto.costoTotal)}</strong></div>
              <div><span>Margen ({producto.margen.toLocaleString("es-AR")}%)</span><strong>{formatoPrecio(producto.importeMargen)}</strong></div>
              <div className="simulation-sale-price"><span>Precio de venta</span><strong>{formatoPrecio(producto.precioVenta)}</strong></div>
              <div className="simulation-approved-price"><span>Precio guardado</span><strong>{precioVentaGuardado(producto) > 0 ? formatoPrecio(precioVentaGuardado(producto)) : "Sin definir"}</strong></div>
            </div>
          </section>
        ))}

        <div className="order-simulation-grand-total">
          <span>Suma de precios de venta</span>
          <strong>{formatoPrecio(simulacion.totalVenta)}</strong>
          {totalVentaGuardado > 0 && <small>Total con precios guardados: {formatoPrecio(totalVentaGuardado)}</small>}
        </div>
      </div>
    </Modal>
  );
}

function OrdenesPanel({ tipo, ordenes, paginacion, titulo, cargando, headerExtra, onDetalle, onPdf, onTracking, onRotulos, onFinalizar, onAnular }) {
  return (
    <section className="panel">
      <div className="panel-title-row">
        <h2>{titulo || ordenTipos[tipo].titulo}</h2>
        {headerExtra}
      </div>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Codigo</th>
              <th>Fecha</th>
              <th>Detalle</th>
              <th>Estado</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {ordenes.length === 0 ? (
              <tr><td colSpan="5">{cargando ? "Cargando..." : "Sin ordenes."}</td></tr>
            ) : (
              ordenes.map((orden) => (
                <tr key={orden._id}>
                  <td><strong>{orden.codigo}</strong></td>
                  <td>{formatDate(orden.fecha)}</td>
                  <td>{detalleOrden(tipo, orden)}</td>
                  <td><span className={`badge ${orden.estado === "finalizada" ? "ok" : orden.estado === "anulada" ? "off" : "warn"}`}>{orden.estado}</span></td>
                  <td>
                      <div className="row-actions">
                        <button className="btn action-icon-button" type="button" title="Detalle" aria-label="Detalle" onClick={() => onDetalle(orden)}><Eye size={16} /></button>
                        <button className="btn action-icon-button" type="button" title="PDF" aria-label="PDF" onClick={() => onPdf(orden)}><FileText size={16} /></button>
                        {["produccion", "fraccionado"].includes(tipo) && (
                          <>
                          </>
                        )}
                      {orden.estado === "pendiente" && (
                        <>
                          <button className="btn action-icon-button" type="button" title="Finalizar" aria-label="Finalizar" onClick={() => onFinalizar(orden)}><CheckCircle2 size={16} /></button>
                          <button className="btn btn-danger action-icon-button" type="button" title="Anular" aria-label="Anular" onClick={() => onAnular(orden)}><XCircle size={16} /></button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <Pagination page={paginacion.page} pageSize={paginacion.pageSize} totalItems={paginacion.totalItems} totalPages={paginacion.totalPages} onPageChange={paginacion.setPage} onPageSizeChange={paginacion.setPageSize} />
    </section>
  );
}

function FiltroFinalizadas({ filtro, onChange }) {
  const setFiltro = (name, value) => onChange((prev) => ({ ...prev, [name]: value }));
  return (
    <div className="finalized-filter">
      <select value={filtro.tipo} onChange={(event) => setFiltro("tipo", event.target.value)}>
        <option value="dia">Dia</option>
        <option value="semana">Semana</option>
        <option value="mes">Mes</option>
        <option value="anio">Año</option>
        <option value="rango">Rango</option>
      </select>
      {filtro.tipo === "rango" ? (
        <>
          <input type="date" value={filtro.desde} onChange={(event) => setFiltro("desde", event.target.value)} />
          <input type="date" value={filtro.hasta} onChange={(event) => setFiltro("hasta", event.target.value)} />
        </>
      ) : filtro.tipo === "semana" ? (
        <input type="week" value={filtro.semana} onChange={(event) => setFiltro("semana", event.target.value)} />
      ) : filtro.tipo === "mes" ? (
        <input type="month" value={filtro.mes} onChange={(event) => setFiltro("mes", event.target.value)} />
      ) : filtro.tipo === "anio" ? (
        <input type="text" inputMode="decimal" min="2000" max="2100" step="1" value={filtro.anio} onChange={(event) => setFiltro("anio", event.target.value)} />
      ) : (
        <input type="date" value={filtro.dia} onChange={(event) => setFiltro("dia", event.target.value)} />
      )}
    </div>
  );
}

function BuscadorCodigo({ value, onChange, clienteValue, onClienteChange, onBuscar }) {
  return (
    <form
      className="code-search"
      onSubmit={(event) => {
        event.preventDefault();
        onBuscar();
      }}
    >
      <div className="search">
        <Search size={17} />
        <input
          aria-label="Buscar por codigo"
          placeholder="Buscar por codigo..."
          value={value}
          onChange={(event) => onChange(event.target.value)}
        />
      </div>
      {onClienteChange && (
        <div className="search">
          <Search size={17} />
          <input
            aria-label="Buscar por cliente"
            placeholder="Buscar por cliente..."
            value={clienteValue || ""}
            onChange={(event) => onClienteChange(event.target.value)}
          />
        </div>
      )}
      <button className="btn" type="submit">
        <Search size={16} />
        Buscar
      </button>
    </form>
  );
}

function OrdenTrackingModal({ tipo, orden, qrDataUrl, onCopiar, onClose }) {
  if (!orden) return null;
  const url = ordenTrackingUrl(tipo, orden.codigo);
  return (
    <Modal
      abierto
      titulo="Tracking de orden"
      subtitulo={orden.codigo || ""}
      size="small"
      onClose={onClose}
      footer={
        <>
          <button className="btn" type="button" onClick={() => onCopiar(orden)}>
            <Copy size={16} />
            Copiar link
          </button>
          <a className="btn btn-primary" href={url} target="_blank" rel="noreferrer">
            <ExternalLink size={16} />
            Abrir
          </a>
        </>
      }
    >
      <div className="tracking-share">
        <div className="tracking-share-code">
          <QrCode size={18} />
          <strong>{orden.codigo}</strong>
        </div>
        {qrDataUrl ? <img src={qrDataUrl} alt="QR tracking orden" /> : <div className="empty-inline">Generando QR...</div>}
        <div className="readonly-link">{url}</div>
      </div>
    </Modal>
  );
}

function OrdenRotulosModal({ tipo, orden, onImprimir, onClose }) {
  const [qrDataUrl, setQrDataUrl] = useState("");
  useEffect(() => {
    if (!orden) return;
    setQrDataUrl("");
    QRCode.toDataURL(ordenTrackingUrl(tipo, orden.codigo), { margin: 1, width: 150 })
      .then(setQrDataUrl)
      .catch(() => setQrDataUrl(""));
  }, [tipo, orden]);

  if (!orden) return null;
  const salidas = tipo === "fraccionado" && orden.salidas?.length ? salidasPorTipo(orden.salidas) : [null];
  const sku = orden.skuProducto || orden.codigo || "-";
  const barcodeUrl = codigoBarrasDataUrl(sku);
  return (
    <Modal
      abierto
      titulo="Rotulos de orden"
      subtitulo={orden.codigo || ""}
      size="wide"
      onClose={onClose}
      footer={<button className="btn" type="button" onClick={onClose}>Cerrar</button>}
    >
      <div className="rotulos-grid">
        {salidas.map((salida, index) => (
          <section className="rotulo-card" key={salida?._id || index}>
            <div className="rotulo-actions">
              <button className="btn btn-primary" type="button" onClick={() => onImprimir(salida, index)}>
                <Printer size={16} />
                Imprimir
              </button>
            </div>
            <div className="rotulo-header">
              <div>
                <span>{tipo === "produccion" ? "Orden de produccion" : "Orden de fraccionamiento"}</span>
                <strong>{orden.codigo}</strong>
              </div>
              <div className="rotulo-type">{tipo === "fraccionado" ? formatPresentacion(salida?.presentacionGramos) : "Producto finalizado"}</div>
            </div>
            <div className="rotulo-product">
              <span>Producto</span>
              <strong>{tipo === "produccion" ? orden.productoTerminado : orden.producto}</strong>
            </div>
            <div className="rotulo-data-grid">
              <RotuloDato label="Lote / partida" value={orden.lotePartidaNro || "-"} />
              <RotuloDato label="Ubicacion" value={orden.ubicacionAsignada || "-"} />
              <RotuloDato
                label="Cantidad"
                value={tipo === "produccion" ? `${orden.kilosProducidos || orden.cantidadProducir || 0} ${orden.unidadMedida || "kg"}` : `${salida?.cantidadPaquetes || 0} paquetes`}
              />
              <RotuloDato
                label="Kg totales"
                value={tipo === "produccion" ? `${orden.kilosProducidos || 0} kg` : `${salida?.kilosTotales || 0} kg`}
              />
              <RotuloDato label="Vencimiento" value={formatDate(orden.fechaVencimiento)} />
              {tipo === "fraccionado" && <RotuloDato label="Presentacion" value={formatPresentacion(salida?.presentacionGramos)} />}
              {tipo === "fraccionado" && <RotuloDato label="OP origen" value={orden.codigoProduccion || "-"} />}
            </div>
            <div className="rotulo-codes">
              <div className="rotulo-barcode">
                <img src={barcodeUrl} alt={`Codigo de barras SKU ${sku}`} />
                <strong>{sku}</strong>
              </div>
              <div className="rotulo-qr">
                {qrDataUrl ? <img src={qrDataUrl} alt={`QR tracking ${orden.codigo}`} /> : <span>QR</span>}
                <small>Tracking</small>
              </div>
            </div>
          </section>
        ))}
      </div>
    </Modal>
  );
}

function RotuloDato({ label, value }) {
  return (
    <div className="rotulo-dato">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function OrdenDetalleModal({ tipo, orden, onClose }) {
  if (!orden) return null;

  return (
    <Modal
      abierto
      titulo={`Detalle ${orden.codigo || ""}`}
      subtitulo={ordenTipos[tipo]?.titulo || "Orden"}
      size="wide"
      onClose={onClose}
      footer={<button className="btn" type="button" onClick={onClose}>Cerrar</button>}
    >
      <div className="order-detail">
        <section className="order-detail-summary">
          <DetailItem label="Codigo" value={orden.codigo} />
          <DetailItem label="Fecha" value={formatDate(orden.fecha)} />
          {orden.estado === "finalizada" && <DetailItem label="Fecha de finalizacion" value={formatDate(orden.fechaFinalizacion || orden.updatedAt)} />}
          <DetailItem label="Estado" value={orden.estado || "pendiente"} />
          {tipo === "pedidos" && <DetailItem label="Cliente" value={orden.clienteNombre} />}
          {tipo === "pedidos" && <DetailItem label="Tipo de cliente" value={orden.clienteTipo || "-"} />}
          {tipo === "pedidos" && <DetailItem label="Precio total" value={formatoPrecio(orden.precioTotal ?? (orden.items || []).reduce((total, item) => total + precioTotalItemPedido(item), 0))} />}
          {tipo === "pedidos" && orden.clienteTipo === "Paciente" && (
            <>
              <DetailItem label="Medico" value={orden.recetaMedica?.nombreMedico} />
              <DetailItem label="Matricula" value={orden.recetaMedica?.matriculaMedico} />
              <DetailItem label="Fecha de emision" value={formatDate(orden.recetaMedica?.fechaEmision)} />
              <DetailItem label="Diagnostico" value={orden.recetaMedica?.diagnostico} />
              <DetailItem label="Observaciones de receta" value={orden.recetaMedica?.observaciones} />
            </>
          )}
          {tipo === "produccion" && (
            <>
              <DetailItem label="Producto a preparar" value={orden.productoTerminado} />
              <DetailItem label="Cantidad producida" value={`${orden.kilosProducidos || orden.cantidadProducir || 0} ${orden.unidadMedida || "kg"}`} />
              <DetailItem label="Lote / Partida Nro" value={orden.lotePartidaNro} />
              <DetailItem label="Ubicacion" value={orden.ubicacionAsignada} />
              <DetailItem label="Fecha de vencimiento" value={formatDate(orden.fechaVencimiento)} />
              <DetailItem label="Cantidad producida" value={`${orden.kilosProducidos || 0} ${orden.unidadMedida || "kg"}`} />
              <DetailItem label="Disponible para fraccionar" value={`${orden.kilosDisponibles ?? orden.kilosProducidos ?? 0} ${orden.unidadMedida || "kg"}`} />
              <DetailItem
                label={`Costo de produccion de 1 ${orden.unidadCostoProduccion || unidadPrecioProducto(orden.tipoMedida)}`}
                value={formatoPrecio(orden.costoProduccionPorUnidad)}
              />
              <DetailItem label="Costo de la cantidad producida" value={formatoPrecio(orden.costoProduccionLote)} />
              <DetailItem label="Costo de materiales" value={formatoPrecio(orden.costoMateriales)} />
              <DetailItem label="Costo total" value={formatoPrecio(orden.costoTotal)} />
              <DetailItem
                label="Margen de ganancia"
                value={`${numero(orden.margenGananciaPorcentaje).toLocaleString("es-AR")}%`}
              />
              <DetailItem label="Precio de venta calculado" value={formatoPrecio(orden.precioVentaCalculado)} />
            </>
          )}
          {tipo === "fraccionado" && (
            <>
              <DetailItem label="Producto a fraccionar" value={orden.producto} />
              <DetailItem label="Orden de produccion" value={orden.codigoProduccion} />
              <DetailItem label="Cantidad" value={orden.cantidad || orden.kilosAFraccionar || 0} />
              <DetailItem label="Lote / Partida Nro" value={orden.lotePartidaNro} />
              <DetailItem label="Ubicacion" value={orden.ubicacionAsignada} />
              <DetailItem label="Fecha de elaboracion" value={formatDate(orden.fechaElaboracion)} />
              <DetailItem label="Fecha de vencimiento" value={formatDate(orden.fechaVencimiento)} />
            </>
          )}
        </section>

        {tipo === "pedidos" && (
          <>
            <DetailTable
              title="Productos del pedido"
              headers={["Producto", "SKU", "Presentacion", "Cant paquetes", "Cantidad total", "Precio por paquete", "Subtotal"]}
              rows={(orden.items || []).map((item) => [
                item.producto,
                item.codigoSku || "-",
                presentacionPedidoTexto(item),
                item.cantidadPaquetes,
                `${item.kilosTotales || kgPedido(item)} ${item.unidadMedida || "kg"}`,
                formatoPrecio(precioPaqueteItemPedido(item)),
                formatoPrecio(item.precioTotal ?? precioTotalItemPedido(item)),
              ])}
            />
            {orden.estado === "finalizada" && <PedidoTrazabilidad trazabilidad={orden.trazabilidad || []} />}
          </>
        )}

        {tipo === "produccion" && (
          <>
            <DetailTable
              title="Ingredientes de la orden"
              headers={["Ingreso", "Materia prima", "Proveedor", "Lote / partida", "Vto", "Cantidad necesaria", "Costo unitario", "Costo"]}
              rows={(orden.insumos || []).map((item) => [
                item.codigoIngreso || "-",
                item.producto || "-",
                item.proveedor || "-",
                item.lotePartidaNro || "-",
                formatDate(item.fechaVencimiento),
                `${item.kilosNecesarios || item.kilos || 0} ${item.unidadMedida || "kg"}`,
                `${formatoPrecio(item.costoUnitario)} / ${item.unidadCosto || unidadPrecioProducto(item.tipoMedida)}`,
                formatoPrecio(item.costoTotal),
              ])}
            />
            <DetailTable
              title="Materiales utilizados"
              headers={["Material", "Cantidad", "Precio unitario", "Precio total"]}
              rows={(orden.materialesEmpaque || []).map((item) => [
                item.nombre || item.tipo || "-",
                item.cantidad || 0,
                formatoPrecio(item.precioUnitario),
                formatoPrecio(item.precioTotal ?? numero(item.cantidad) * numero(item.precioUnitario)),
              ])}
            />
          </>
        )}

        {tipo === "fraccionado" && (
          <DetailTable
            title="Paquetes resultantes"
            headers={["Presentacion", "Cant paquetes", "Kg totales"]}
            rows={(orden.salidas || []).map((item) => [
              formatPresentacion(item.presentacionGramos),
              item.cantidadPaquetes,
              `${item.kilosTotales || 0} kg`,
            ])}
          />
        )}


        <section className="order-detail-notes">
          <h3>Observaciones</h3>
          <p>{orden.observaciones || "-"}</p>
        </section>
      </div>
    </Modal>
  );
}

function filasIngredientesTrazabilidad(produccion, ruta = []) {
  return (produccion?.insumos || []).flatMap((insumo) => {
    const ingreso = insumo.ingreso || {};
    const produccionOrigen = insumo.ordenProduccionOrigen;
    const camino = [...ruta, insumo.producto || "Ingrediente"];
    const fila = [
      camino.join(" > "),
      produccionOrigen?.codigo || "-",
      ingreso.codigo || insumo.codigoIngreso || "-",
      ingreso.proveedor || insumo.proveedor || "-",
      ingreso.lotePartidaNro || insumo.lotePartidaNro || "-",
      formatDate(ingreso.fechaIngreso),
      formatDate(ingreso.fechaElaboracion || insumo.fechaElaboracion),
      formatDate(ingreso.fechaVencimiento || insumo.fechaVencimiento),
      `${insumo.cantidadNecesaria || 0} ${insumo.unidadMedida || "kg"}`,
      ingreso.ubicacionAsignada || "-",
    ];
    return produccionOrigen
      ? [fila, ...filasIngredientesTrazabilidad(produccionOrigen, camino)]
      : [fila];
  });
}

function PedidoTrazabilidad({ trazabilidad }) {
  const items = Array.isArray(trazabilidad) ? trazabilidad : [];
  return (
    <section className="order-detail-section">
      <h3>Trazabilidad del pedido</h3>
      {items.length === 0 ? (
        <div className="trace-empty">Sin trazabilidad registrada. Se genera al finalizar pedidos nuevos.</div>
      ) : (
        <div className="trace-list">
          {items.map((item, itemIndex) => (
            <article className="trace-card" key={`${item.producto}-${itemIndex}`}>
              <div className="trace-card-header">
                <strong>{item.producto}</strong>
                <span>
                  {item.cantidadPaquetes || 0} paquetes de {etiquetaUnidad(item.cantidadPorPaquete, item.unidadMedida)} / {etiquetaUnidad(item.cantidadTotal, item.unidadMedida)}
                </span>
              </div>
              {(item.asignaciones || []).map((asignacion, asignacionIndex) => (
                <TrazabilidadAsignacion asignacion={asignacion} key={asignacionIndex} />
              ))}
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

function TrazabilidadAsignacion({ asignacion }) {
  const fraccionado = asignacion.ordenFraccionado;
  const produccion = asignacion.ordenProduccion || fraccionado?.ordenProduccion;
  return (
    <div className="trace-source">
      <div className="trace-source-amount">
        <span>Cantidad utilizada</span>
        <strong>{asignacion.cantidadPaquetes || 0} paquetes / {etiquetaUnidad(asignacion.cantidad, asignacion.unidadMedida)}</strong>
      </div>
      {fraccionado && (
        <>
          <div className="trace-summary">
            <strong>Orden de fraccionamiento {fraccionado.codigo || "-"}</strong>
            <span>{fraccionado.producto || "-"}</span>
          </div>
          <div className="trace-data-grid">
            <DetailItem label="Fecha" value={formatDate(fraccionado.fecha)} />
            <DetailItem label="Finalizacion" value={formatDate(fraccionado.fechaFinalizacion)} />
            <DetailItem label="Lote / partida" value={fraccionado.lotePartidaNro} />
            <DetailItem label="Elaboracion" value={formatDate(fraccionado.fechaElaboracion)} />
            <DetailItem label="Vencimiento" value={formatDate(fraccionado.fechaVencimiento)} />
            <DetailItem label="Ubicacion" value={fraccionado.ubicacionAsignada} />
            <DetailItem label="Cantidad fraccionada" value={`${fraccionado.cantidad || 0} kg`} />
          </div>
        </>
      )}
      {produccion && (
        <>
          <div className="trace-summary">
            <strong>Orden de produccion {produccion.codigo || "-"}</strong>
            <span>{produccion.productoTerminado || "-"}</span>
          </div>
          <div className="trace-data-grid">
            <DetailItem label="Fecha" value={formatDate(produccion.fecha)} />
            <DetailItem label="Finalizacion" value={formatDate(produccion.fechaFinalizacion)} />
            <DetailItem label="Lote / partida" value={produccion.lotePartidaNro} />
            <DetailItem label="Vencimiento" value={formatDate(produccion.fechaVencimiento)} />
            <DetailItem label="Ubicacion" value={produccion.ubicacionAsignada} />
            <DetailItem label="Cantidad producida" value={etiquetaUnidad(produccion.cantidadProducida, produccion.unidadMedida)} />
            <DetailItem label="Unidad por paquete" value={etiquetaUnidad(produccion.cantidadPorPaquete, produccion.unidadMedida)} />
          </div>
          <DetailTable
            title="Ingredientes e ingresos de la orden de produccion"
            headers={["Ingrediente", "OP intermedia", "Ingreso", "Proveedor", "Lote / partida", "Fecha ingreso", "Elaboracion", "Vencimiento", "Cantidad", "Ubicacion"]}
            rows={filasIngredientesTrazabilidad(produccion)}
          />
        </>
      )}
    </div>
  );
}

function DetailItem({ label, value }) {
  const visibleValue = value === null || value === undefined || value === "" ? "-" : value;
  return (
    <div className="detail-item">
      <span>{label}</span>
      <strong>{visibleValue}</strong>
    </div>
  );
}

function DetailTable({ title, headers, rows }) {
  return (
    <section className="order-detail-section">
      <h3>{title}</h3>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              {headers.map((header) => <th key={header}>{header}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={headers.length}>Sin datos.</td></tr>
            ) : (
              rows.map((row, index) => (
                <tr key={index}>
                  {row.map((cell, cellIndex) => <td key={cellIndex}>{cell}</td>)}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

const detalleOrden = (tipo, orden) => {
  if (tipo === "pedidos") return `${orden.clienteNombre} - ${(orden.items || []).length} items`;
  if (tipo === "produccion") return `${orden.productoTerminado} - ${orden.kilosProducidos || orden.cantidadProducir || 0} ${orden.unidadMedida || "kg"}`;
  if (tipo === "fraccionado") return `${orden.producto} - ${orden.cantidad || orden.kilosAFraccionar} kg`;
  return "-";
};

function OrdenModal(props) {
  const {
    tipo,
    formulario,
    clientes,
    productosTerminados,
    productosProducidos = [],
    productosCatalogo = [],
    proveedores = [],
    productosVenta,
    preciosPedido = [],
    ordenesProduccion = [],
    ingresosMercaderia = [],
    stocks = { materiaPrima: [] },
    materialesProduccion = [],
    cargando,
    error,
    recipeAlert,
    onError,
    onRecipeAlert,
    onClose,
    onSubmit,
    setCampo,
    setItem,
    agregarItem,
    quitarItem,
  } = props;

  if (!tipo || !formulario) return null;

  return (
    <Modal
      abierto
      titulo={`Nueva ${ordenTipos[tipo].singular}`}
      subtitulo={tipo === "produccion" ? "" : "La orden impacta stock solo cuando se finaliza."}
      size={tipo === "produccion" ? "production" : "wide"}
      onClose={onClose}
      footer={
        <>
          {error && <div className="modal-footer-error alert alert-error" role="alert">{error}</div>}
          <button className="btn" type="button" onClick={onClose}>Cancelar</button>
          {["produccion", "fraccionado"].includes(tipo) && (
            <button className="btn btn-green" form="orden-operativa-form" type="submit" value="finalizar" disabled={cargando}>Guardar y finalizar</button>
          )}
          <button className="btn btn-primary" form="orden-operativa-form" type="submit" value="guardar" disabled={cargando}>Guardar orden</button>
        </>
      }
    >
      {error && <div className="alert alert-error">{error}</div>}
      <form id="orden-operativa-form" onSubmit={onSubmit}>
        {tipo === "pedidos" && (
          <PedidoForm formulario={formulario} clientes={clientes} productosVenta={productosVenta} preciosPedido={preciosPedido} setCampo={setCampo} setItem={setItem} agregarItem={agregarItem} quitarItem={quitarItem} />
        )}
        {tipo === "produccion" && (
          <ProduccionForm
            formulario={formulario}
            ingresosMercaderia={ingresosMercaderia}
            stocks={stocks}
            productosProducidos={productosProducidos}
            productosCatalogo={productosCatalogo}
            proveedores={proveedores}
            materialesProduccion={materialesProduccion}
            recipeAlert={recipeAlert}
            onError={onError}
            onRecipeAlert={onRecipeAlert}
            setCampo={setCampo}
            setItem={setItem}
            agregarItem={agregarItem}
            quitarItem={quitarItem}
          />
        )}
        {tipo === "fraccionado" && (
          <FraccionadoForm formulario={formulario} productosTerminados={productosTerminados} productosCatalogo={productosCatalogo} ordenesProduccion={ordenesProduccion} stocks={stocks} setCampo={setCampo} setItem={setItem} agregarItem={agregarItem} quitarItem={quitarItem} />
        )}
      </form>
    </Modal>
  );
}

function Field({ label, ...props }) {
  return (
    <div className="field">
      <label>{label}</label>
      <input type="text" {...props} />
    </div>
  );
}

function SelectInput({ label, options, ...props }) {
  return (
    <div className="field">
      <label>{label}</label>
      <select {...props}>
        <option value="">Seleccionar</option>
        {options.map((option) => (
          <option key={option.value || option} value={option.value || option}>{option.label || option}</option>
        ))}
      </select>
    </div>
  );
}

function SearchableSelect({ label, value, options, onChange, placeholder = "Buscar..." }) {
  const [texto, setTexto] = useState("");
  const [abierto, setAbierto] = useState(false);
  const seleccion = options.find((option) => String(getOptionValue(option)) === String(value || ""));
  const query = texto.trim().toLowerCase();
  const filtradas = options
    .filter((option) => getOptionLabel(option).toLowerCase().includes(query))
    .slice(0, 12);

  useEffect(() => {
    setTexto(seleccion ? getOptionLabel(seleccion) : "");
  }, [value, seleccion]);

  const elegir = (option) => {
    setTexto(getOptionLabel(option));
    setAbierto(false);
    onChange(getOptionValue(option), option);
  };

  return (
    <div className="field searchable-select">
      <label>{label}</label>
      <input
        type="text"
        value={texto}
        placeholder={placeholder}
        onFocus={() => setAbierto(true)}
        onBlur={() => window.setTimeout(() => setAbierto(false), 120)}
        onChange={(event) => {
          setTexto(event.target.value);
          setAbierto(true);
          onChange("", null);
        }}
        autoComplete="off"
      />
      {abierto && (
        <div className="searchable-options">
          {filtradas.length === 0 ? (
            <div className="searchable-empty">Sin resultados</div>
          ) : (
            filtradas.map((option) => (
              <button
                key={getOptionValue(option)}
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => elegir(option)}
              >
                {getOptionLabel(option)}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function PedidoForm({ formulario, clientes, productosVenta, preciosPedido, setCampo, setItem, agregarItem, quitarItem }) {
  const clienteSeleccionado = clientes.find((cliente) => cliente._id === formulario.clienteId);
  const esPaciente = (clienteSeleccionado?.tipoCliente || formulario.clienteTipo) === "Paciente";
  const setReceta = (name, value) =>
    setCampo("recetaMedica", { ...(formulario.recetaMedica || {}), [name]: value });
  return (
    <>
      <section className="form-section">
        <h3>Cliente</h3>
        <div className="form-grid">
          <SearchableSelect label="Cliente" value={formulario.clienteId} placeholder="Buscar cliente..." onChange={(value) => {
            const cliente = clientes.find((item) => item._id === value);
            setCampo("clienteId", value);
            setCampo("clienteNombre", nombreVisibleCliente(cliente));
            setCampo("clienteTipo", cliente?.tipoCliente || "");
            if (cliente?.tipoCliente !== "Paciente") {
              setCampo("recetaMedica", formulariosIniciales.pedidos().recetaMedica);
            }
          }} options={clientes.map((cliente) => ({ value: cliente._id, label: nombreVisibleCliente(cliente) }))} />
          <Field label="Fecha" type="date" value={formulario.fecha} onChange={(e) => setCampo("fecha", e.target.value)} />
        </div>
      </section>
      {esPaciente && (
        <section className="form-section">
          <h3>Receta medica</h3>
          <div className="form-grid">
            <Field label="Nombre del medico" value={formulario.recetaMedica?.nombreMedico || ""} onChange={(e) => setReceta("nombreMedico", e.target.value)} />
            <Field label="Matricula del medico" value={formulario.recetaMedica?.matriculaMedico || ""} onChange={(e) => setReceta("matriculaMedico", e.target.value)} />
            <Field label="Fecha de emision" type="date" value={formulario.recetaMedica?.fechaEmision || ""} onChange={(e) => setReceta("fechaEmision", e.target.value)} />
            <Field label="Diagnostico" value={formulario.recetaMedica?.diagnostico || ""} onChange={(e) => setReceta("diagnostico", e.target.value)} />
            <div className="field field-span-full">
              <label>Observaciones</label>
              <textarea rows="3" value={formulario.recetaMedica?.observaciones || ""} onChange={(e) => setReceta("observaciones", e.target.value)} />
            </div>
          </div>
        </section>
      )}
      <ItemsPedido formulario={formulario} productosVenta={productosVenta} preciosPedido={preciosPedido} setCampo={setCampo} setItem={setItem} agregarItem={agregarItem} quitarItem={quitarItem} />
    </>
  );
}

function ItemsPedido({ formulario, productosVenta, preciosPedido, setItem, agregarItem, quitarItem }) {
  const productoCatalogoParaItem = (item) => (productosVenta || []).find(
    (producto) =>
      String(producto.productoId || "") === String(item.productoId || "") ||
      normalizarProducto(producto.label) === normalizarProducto(item.producto),
  );
  const seleccionarProducto = (index, value) => {
    const producto = productosVenta.find((option) => getOptionValue(option) === value);
    setItem("items", index, "productoId", producto?.productoId || "");
    setItem("items", index, "codigoSku", producto?.codigoSku || "");
    setItem("items", index, "producto", value);
    setItem("items", index, "tipoMedida", producto?.tipoMedida || "Peso");
    setItem("items", index, "unidadMedida", producto?.unidadMedida || "kg");
    setItem("items", index, "kilosPorPaquete", "");
  };
  const precioParaItem = (item) => {
    const productoCatalogo = productoCatalogoParaItem(item);
    const automatico = (preciosPedido || []).find(
      (producto) =>
        String(producto.productoId || "") === String(item.productoId || "") ||
        normalizarProducto(producto.nombre) === normalizarProducto(item.producto),
    );
    const precioPorUnidad = numero(item.precioPorUnidad) > 0
      ? numero(item.precioPorUnidad)
      : (numero(productoCatalogo?.precioVentaPorUnidad) > 0
          ? numero(productoCatalogo.precioVentaPorUnidad)
          : numero(automatico?.precioPorUnidad));
    const cantidadPaqueteUnidadPrecio = convertirUnidad(
      numero(item.kilosPorPaquete),
      item.unidadMedida,
      item.tipoMedida === "Volumen" ? "l" : "kg",
    );
    const tienePrecioManual = item.precioPorPaqueteManual !== null &&
      item.precioPorPaqueteManual !== undefined;
    const precioPorPaquete = tienePrecioManual
      ? item.precioPorPaqueteManual
      : cantidadPaqueteUnidadPrecio * precioPorUnidad;
    return {
      precioPorPaquete,
      subtotal: numero(precioPorPaquete) * numero(item.cantidadPaquetes),
    };
  };
  const precioTotalPedido = (formulario.items || []).reduce(
    (total, item) => total + precioParaItem(item).subtotal,
    0,
  );
  const cambiarPrecioPaquete = (index, item, value) => {
    const cantidadPaqueteUnidadPrecio = convertirUnidad(
      numero(item.kilosPorPaquete),
      item.unidadMedida,
      item.tipoMedida === "Volumen" ? "l" : "kg",
    );
    setItem("items", index, "precioPorPaqueteManual", value);
    setItem(
      "items",
      index,
      "precioPorUnidad",
      cantidadPaqueteUnidadPrecio > 0 ? numero(value) / cantidadPaqueteUnidadPrecio : 0,
    );
  };
  const cambiarPresentacion = (index, item, value) => {
    const producto = productoCatalogoParaItem(item);
    const presentacion = opcionesPresentacionPedido(producto || item).find(
      (option) => String(option.value) === String(value),
    );
    setItem("items", index, "kilosPorPaquete", value);
    if (numero(presentacion?.precioVenta) > 0) {
      cambiarPrecioPaquete(index, { ...item, kilosPorPaquete: value }, presentacion.precioVenta);
    } else {
      setItem("items", index, "precioPorPaqueteManual", null);
      setItem("items", index, "precioPorUnidad", 0);
    }
  };
  return (
    <section className="form-section">
      <div className="section-title-actions">
        <h3>Productos del pedido</h3>
        <button className="btn" type="button" onClick={() => agregarItem("items", itemPedidoVacio)}><Plus size={17} />Agregar</button>
      </div>
      <div className="ops-line-list">
        {formulario.items.map((item, index) => {
          const precio = precioParaItem(item);
          const productoCatalogo = productoCatalogoParaItem(item);
          return (
          <div className="ops-line pedido-line" key={index}>
            <SearchableSelect label="Producto" value={item.producto} onChange={(value) => seleccionarProducto(index, value)} options={productosVenta} />
            <Field label="Paquetes" type="text" inputMode="decimal" step="1" value={item.cantidadPaquetes} onChange={(e) => setItem("items", index, "cantidadPaquetes", e.target.value)} />
            <SelectInput label="Presentacion" value={item.kilosPorPaquete} onChange={(e) => cambiarPresentacion(index, item, e.target.value)} options={opcionesPresentacionPedido(productoCatalogo || item)} />
            <Field
              label="Precio por paquete"
              type="text"
              inputMode="decimal"
              min="0"
              step="0.01"
              value={precio.precioPorPaquete}
              onChange={(e) => cambiarPrecioPaquete(index, item, e.target.value)}
            />
            <Field label="Subtotal" value={formatoPrecio(precio.subtotal)} disabled />
            <button className="btn btn-danger product-remove" type="button" onClick={() => quitarItem("items", index, itemPedidoVacio)}><XCircle size={16} /></button>
          </div>
          );
        })}
      </div>
      <div className="order-price-total">
        <span>Precio total del pedido</span>
        <strong>{formatoPrecio(precioTotalPedido)}</strong>
      </div>
    </section>
  );
}

function RecetaResultado({ alerta }) {
  if (!alerta) return null;
  const encontrados = alerta.encontrados || [];
  const faltantes = alerta.faltantes || [];
  return (
    <div className="recipe-result">
      <div>
        <strong>Resultado de receta</strong>
        <p>La receta no se puede completar. Se muestran los ingredientes disponibles y faltantes.</p>
      </div>
      {encontrados.length > 0 && (
        <div className="recipe-result-list">
          {encontrados.map((item) => (
            <div className="recipe-result-item" key={item.producto}>
              <span>{item.producto}</span>
              <small>{item.kilosNecesarios} {item.unidadMedida || "kg"} - {item.ingresoCodigo || "ingreso disponible"}</small>
            </div>
          ))}
        </div>
      )}
      {faltantes.length > 0 && (
        <div className="recipe-result-list">
          {faltantes.map((item) => (
            <div className="recipe-result-item recipe-result-missing" key={item.producto}>
              <span>{item.producto}</span>
              <small>{item.motivo} Faltan {item.faltante} {item.unidadMedida || "kg"}.</small>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ProduccionForm({
  formulario,
  ingresosMercaderia,
  stocks,
  productosProducidos,
  productosCatalogo,
  proveedores,
  materialesProduccion,
  recipeAlert,
  onError,
  onRecipeAlert,
  setCampo,
  setItem,
  agregarItem,
  quitarItem,
}) {
  const productosIngredientes = (productosCatalogo || [])
    .filter(
      (producto) =>
        producto.activo !== false && ["Materia prima", "Intermedio"].includes(producto.tipo),
    )
    .map((producto) => ({
      value: producto._id,
      label: `${producto.codigoSku || "Sin SKU"} - ${producto.nombre}`,
      producto,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
  const productoCatalogoDe = (item) =>
    (productosCatalogo || []).find(
      (producto) =>
        (item?.productoId && String(producto._id) === String(item.productoId)) ||
        normalizarProducto(producto.nombre) === normalizarProducto(item?.producto),
    );
  const ingresosStockInicialParaProducto = (producto) => (ingresosMercaderia || []).filter(
    (ingreso) =>
      ingreso.esStockInicial === true &&
      ingreso.estado !== "anulado" &&
      ((ingreso.productoCatalogoId &&
        String(ingreso.productoCatalogoId) === String(producto?._id || "")) ||
        (!ingreso.productoCatalogoId &&
          normalizarProducto(ingreso.producto) === normalizarProducto(producto?.nombre))),
  );
  const ingresoDeLote = (lote) => (ingresosMercaderia || []).find(
    (ingreso) => String(ingreso._id) === String(lote?.id || ""),
  );
  const proveedoresParaProducto = (producto) => {
    if (!producto) return [];
    if (producto.tipo === "Intermedio") {
      const { lotes } = lotesParaIntermedio(producto);
      const opciones = [];
      if (lotes.some((lote) => lote.origenTipo !== "ingreso")) {
        opciones.push({
          value: "produccion-interna",
          label: "Produccion interna",
          proveedor: null,
          precio: 0,
        });
      }
      if (lotes.some((lote) => lote.origenTipo === "ingreso")) {
        opciones.push({
          value: "stock-inicial",
          label: "Stock inicial",
          proveedor: null,
          precio: 0,
        });
      }
      return opciones.length ? opciones : [{
        value: "produccion-interna",
        label: "Produccion interna",
        proveedor: null,
        precio: 0,
      }];
    }

    const opciones = (proveedores || [])
      .map((proveedor) => {
        const asociado = (proveedor.productos || []).find(
          (item) =>
            item.activo !== false &&
            ((idProductoProveedor(item) &&
              idProductoProveedor(item) === String(producto._id)) ||
              normalizarProducto(item.nombre) === normalizarProducto(producto.nombre)),
        );
        if (!asociado) return null;
        return {
          value: proveedor._id,
          label: `${proveedor.razonSocial} - ${formatoPrecio(
            asociado.precioCompraPorUnidad ?? asociado.precioPorKg,
          )}`,
          proveedor,
          productoProveedor: asociado,
          precio: numero(asociado.precioCompraPorUnidad ?? asociado.precioPorKg),
        };
      })
      .filter(Boolean)
      .sort((a, b) => {
        if (a.precio <= 0 && b.precio > 0) return 1;
        if (b.precio <= 0 && a.precio > 0) return -1;
        return a.precio - b.precio || a.label.localeCompare(b.label);
      });
    if (ingresosStockInicialParaProducto(producto).length) {
      opciones.push({
        value: "stock-inicial",
        label: "Stock inicial",
        proveedor: null,
        precio: 0,
      });
    }
    return opciones;
  };
  const ingresosParaProductoProveedor = (producto, proveedorId) => {
    if (proveedorId === "stock-inicial") return ingresosStockInicialParaProducto(producto);
    const proveedorSeleccionado = (proveedores || []).find(
      (proveedor) => String(proveedor._id) === String(proveedorId),
    );
    return (ingresosMercaderia || [])
      .filter(
        (ingreso) =>
          ingreso.tipoIngreso !== "material" &&
          ingreso.estado !== "anulado" &&
          normalizarProducto(ingreso.producto) === normalizarProducto(producto?.nombre) &&
          (idProveedorIngreso(ingreso) === String(proveedorId || "") ||
            (!idProveedorIngreso(ingreso) &&
              normalizarProducto(ingreso.proveedor) ===
                normalizarProducto(proveedorSeleccionado?.razonSocial))),
      )
      .sort((a, b) => new Date(b.fechaIngreso || b.createdAt) - new Date(a.fechaIngreso || a.createdAt));
  };
  const lotesParaIntermedio = (producto, proveedorId = "") => {
    const stock = (stocks?.materiaPrima || []).find(
      (item) =>
        item.tipo === "Intermedio" &&
        normalizarProducto(item.producto) === normalizarProducto(producto?.nombre),
    );
    const lotes = (stock?.lotes || []).filter((lote) => numero(lote.disponible) > 0);
    return {
      stock,
      lotes: proveedorId === "stock-inicial"
        ? lotes.filter((lote) => lote.origenTipo === "ingreso")
        : proveedorId === "produccion-interna"
          ? lotes.filter((lote) => lote.origenTipo !== "ingreso")
          : lotes,
    };
  };
  const obtenerFuenteParaProducto = (productoNombre, tipo, productoId) => {
    const producto =
      (productosCatalogo || []).find((item) => String(item._id) === String(productoId || "")) ||
      (productosCatalogo || []).find(
        (item) => normalizarProducto(item.nombre) === normalizarProducto(productoNombre),
      );
    if (tipo === "Intermedio") {
      const { stock, lotes } = lotesParaIntermedio(producto);
      const lote = lotes[0] || null;
      return {
        origenTipo: lote?.origenTipo === "ingreso" ? "mercaderia" : "produccion",
        lote,
        ingresoInicial: ingresoDeLote(lote),
        producto: producto?.nombre || productoNombre,
        productoCatalogo: producto,
        proveedorInterno: true,
        stock,
      };
    }
    const proveedorOpcion = proveedoresParaProducto(producto).find(
      (opcion) => ingresosParaProductoProveedor(producto, opcion.value).length > 0,
    ) || null;
    const ingreso = proveedorOpcion
      ? ingresosParaProductoProveedor(producto, proveedorOpcion.value)[0] || null
      : null;
    const stock = (stocks?.materiaPrima || []).find(
      (item) => normalizarProducto(item.producto) === normalizarProducto(productoNombre),
    );
    return {
      origenTipo: "mercaderia",
      ingreso,
      producto: producto?.nombre || productoNombre,
      productoCatalogo: producto,
      proveedorOpcion,
      stock,
    };
  };
  const obtenerStockMateria = (producto) =>
    (stocks?.materiaPrima || []).find((item) => normalizarProducto(item.producto) === normalizarProducto(producto));
  const loteStockDeIngreso = (producto, ingresoId) => {
    const stock = obtenerStockMateria(producto?.nombre);
    return (stock?.lotes || []).find(
      (lote) => lote.origenTipo === "ingreso" && String(lote.id) === String(ingresoId || ""),
    ) || null;
  };
  const disponibilidadFuente = (fuente) => {
    const lote = fuente?.lote || loteStockDeIngreso(
      fuente?.productoCatalogo,
      fuente?.ingreso?._id || fuente?.ingresoInicial?._id,
    );
    const unidadDestino = fuente?.stock?.unidadMedida || fuente?.productoCatalogo?.unidadMedida || lote?.unidad || "kg";
    return {
      cantidad: convertirUnidad(numero(lote?.disponible), lote?.unidad || unidadDestino, unidadDestino),
      unidad: unidadDestino,
      lote,
    };
  };
  const fuentesDisponiblesParaProducto = (producto) => {
    if (!producto) return [];
    if (producto.tipo === "Intermedio") {
      const { stock, lotes } = lotesParaIntermedio(producto);
      return lotes.map((lote) => ({
        origenTipo: lote.origenTipo === "ingreso" ? "mercaderia" : "produccion",
        lote,
        ingresoInicial: lote.origenTipo === "ingreso" ? ingresoDeLote(lote) : null,
        producto: producto.nombre,
        productoCatalogo: producto,
        proveedorInterno: true,
        stock,
      }));
    }

    const stock = obtenerStockMateria(producto.nombre);
    const usados = new Set();
    return proveedoresParaProducto(producto).flatMap((proveedorOpcion) =>
      ingresosParaProductoProveedor(producto, proveedorOpcion.value).flatMap((ingreso) => {
        const id = String(ingreso._id || "");
        const lote = loteStockDeIngreso(producto, id);
        if (!id || usados.has(id) || numero(lote?.disponible) <= 0) return [];
        usados.add(id);
        return [{
          origenTipo: "mercaderia",
          ingreso,
          lote,
          producto: producto.nombre,
          productoCatalogo: producto,
          proveedorOpcion,
          stock,
        }];
      }),
    );
  };
  const productoSeleccionado = productosProducidos.find((item) => item.value === formulario.productoTerminado);
  const rendimientoSeleccionado = productoSeleccionado?.recetaRendimiento || {};
  const cantidadRendimiento = numero(rendimientoSeleccionado.cantidad);
  const unidadRendimiento = rendimientoSeleccionado.unidadMedida || "kg";
  const etiquetaCantidadOrden = `Cantidad a producir (${unidadRendimiento})`;
  const unidadCostoProduccion = unidadPrecioProducto(productoSeleccionado?.tipoMedida || "Peso");
  const cantidadProducidaCosto = convertirUnidad(
    numero(formulario.cantidadProducir),
    unidadRendimiento,
    unidadCostoProduccion,
  );
  const costoIngredientes = (formulario.insumos || []).reduce((total, item) => {
    const unidadCosto = item.unidadCosto || unidadPrecioProducto(item.tipoMedida || "Peso");
    const cantidadCosto = convertirUnidad(item.kilosNecesarios, item.unidadMedida || unidadCosto, unidadCosto);
    return total + cantidadCosto * numero(item.costoUnitario);
  }, 0);
  const costoTotalIngrediente = (item) => {
    const unidadCosto = item.unidadCosto || unidadPrecioProducto(item.tipoMedida || "Peso");
    const cantidadCosto = convertirUnidad(item.kilosNecesarios, item.unidadMedida || unidadCosto, unidadCosto);
    return cantidadCosto * numero(item.costoUnitario);
  };
  const costoMateriales = (formulario.materialesEmpaque || []).reduce(
    (total, item) => total + numero(item.cantidad) * numero(item.precioUnitario),
    0,
  );
  const costoProduccionPorUnidad = cantidadProducidaCosto > 0 ? costoIngredientes / cantidadProducidaCosto : 0;
  const costoTotalOrden = costoIngredientes + costoMateriales;
  const margenGananciaPorcentaje = Math.max(numero(formulario.margenGananciaPorcentaje), 0);
  const importeMargen = costoTotalOrden * (margenGananciaPorcentaje / 100);
  const precioVentaCalculado = costoTotalOrden + importeMargen;
  const cantidadIngredienteFormula = (item, cantidadPaquetes, productoProducido) => {
    const cantidadBase = numero(item.kilosPorPaquete25kg ?? item.cantidad);
    const cantidad = numero(cantidadPaquetes);
    const stock = obtenerStockMateria(item.producto);
    const unidadDestino = stock?.unidadMedida || item.unidadMedida || "kg";
    return convertirUnidad(cantidadBase * cantidad, item.unidadMedida || unidadDestino, unidadDestino);
  };
  const insumoDesdeReceta = (
    item,
    cantidadPaquetes,
    productoProducido,
    fuenteForzada = null,
    cantidadForzada = null,
  ) => {
    const fuente = fuenteForzada || obtenerFuenteParaProducto(item.producto, item.tipo, item.productoId);
    const ingreso = fuente?.ingreso;
    const lote = fuente?.lote;
    const ingresoInicial = fuente?.ingresoInicial;
    const esStockInicial = lote?.origenTipo === "ingreso" && ingresoInicial;
    const costoUnitario = ingreso || ingresoInicial
      ? precioCompraIngreso(ingreso || ingresoInicial)
      : lote
        ? numero(lote.costoUnitario)
        : numero(fuente?.proveedorOpcion?.precio);
    const unidadCosto = ingreso || ingresoInicial
      ? unidadPrecioProducto(item.tipoMedida || fuente?.stock?.tipoMedida || "Peso")
      : lote?.unidadCosto || unidadPrecioProducto(item.tipoMedida || fuente?.stock?.tipoMedida || "Peso");
    const kilosNecesarios = cantidadForzada === null
      ? cantidadIngredienteFormula(item, cantidadPaquetes, productoProducido)
      : numero(cantidadForzada);
    return {
      ...insumoVacio(),
      origenTipo: esStockInicial ? "mercaderia" : fuente?.origenTipo || "mercaderia",
      mercaderiaId: ingreso?._id || ingresoInicial?._id || "",
      ordenProduccionId: fuente?.origenTipo === "produccion" ? lote?.id || "" : "",
      codigoIngreso: ingreso?.codigoTracking || ingresoInicial?.codigoTracking || lote?.codigo || "",
      productoId: fuente?.productoCatalogo?._id || item.productoId || "",
      producto: ingreso?.producto || fuente?.producto || item.producto || "",
      tipoProducto: item.tipo || fuente?.productoCatalogo?.tipo || "",
      proveedorId: fuente?.proveedorOpcion?.proveedor?._id || idProveedorIngreso(ingreso),
      proveedor:
        ingreso?.proveedor ||
        ingresoInicial?.proveedor ||
        fuente?.proveedorOpcion?.proveedor?.razonSocial ||
        (fuente?.proveedorInterno ? "Produccion interna" : ""),
      rne: ingreso?.rne || "",
      rnpa: ingreso?.rnpa || "",
      lotePartidaNro: ingreso?.lotePartidaNro || ingresoInicial?.lotePartidaNro || lote?.lote || "",
      fechaElaboracion: dateInputValue(ingreso?.fechaElaboracion),
      fechaVencimiento: dateInputValue(ingreso?.fechaVencimiento),
      kilosNecesarios: kilosNecesarios ? String(kilosNecesarios) : "",
      tipoMedida: item.tipoMedida || fuente?.stock?.tipoMedida || "",
      unidadMedida: fuente?.stock?.unidadMedida || item.unidadMedida || "kg",
      unidadCosto,
      costoUnitario,
    };
  };
  const insumosDistribuidosDesdeReceta = (item, cantidadPaquetes, productoProducido) => {
    const producto = productoCatalogoDe(item);
    const cantidadTotal = cantidadIngredienteFormula(item, cantidadPaquetes, productoProducido);
    const fuentes = fuentesDisponiblesParaProducto(producto);
    if (!fuentes.length || cantidadTotal <= 0) {
      return [insumoDesdeReceta(item, cantidadPaquetes, productoProducido)];
    }

    let restante = cantidadTotal;
    const filas = [];
    for (const fuente of fuentes) {
      if (restante <= 0.0000001) break;
      const disponible = disponibilidadFuente(fuente).cantidad;
      if (disponible <= 0) continue;
      const cantidadAsignada = Math.min(restante, disponible);
      filas.push(insumoDesdeReceta(
        item,
        cantidadPaquetes,
        productoProducido,
        fuente,
        cantidadAsignada,
      ));
      restante = Math.max(0, restante - cantidadAsignada);
    }

    if (restante > 0.0000001 && filas.length) {
      const ultima = filas[filas.length - 1];
      ultima.kilosNecesarios = String(numero(ultima.kilosNecesarios) + restante);
    }
    return filas.length ? filas : [insumoDesdeReceta(item, cantidadPaquetes, productoProducido)];
  };
  const materialesDesdeReceta = (productoProducido, cantidadPaquetes) => {
    const cantidadProducciones = numero(cantidadPaquetes);
    const materiales = (productoProducido?.materialesProduccion || [])
      .map((item) => {
        const material = (materialesProduccion || []).find(
          (candidate) => String(candidate._id) === String(item.materialId),
        );
        const cantidad = numero(item.cantidad) * cantidadProducciones;
        const precioUnitario = numero(material?.precio);
        return {
          ...materialEmpaqueVacio(),
          materialId: material?._id || item.materialId || "",
          nombre: material?.nombre || item.nombre || "",
          descripcion: material?.descripcion || "",
          cantidad: cantidad ? String(cantidad) : "",
          precioUnitario,
          precioTotal: cantidad * precioUnitario,
        };
      })
      .filter((item) => item.materialId && numero(item.cantidad) > 0);
    return materiales.length ? materiales : [materialEmpaqueVacio()];
  };
  const aplicarReceta = (producto, cantidadPaquetes) => {
    const productoProducido = productosProducidos.find((item) => item.value === producto);
    if (!productoProducido || numero(cantidadPaquetes) <= 0) {
      onRecipeAlert?.(null);
      return;
    }

    setCampo("materialesEmpaque", materialesDesdeReceta(productoProducido, cantidadPaquetes));

    const receta = productoProducido.receta || [];
    if (receta.length === 0) {
      setCampo("insumos", [insumoVacio()]);
      onRecipeAlert?.(null);
      onError?.("");
      return;
    }

    const resultado = receta.map((item) => {
      const fuente = obtenerFuenteParaProducto(item.producto, item.tipo, item.productoId);
      const cantidadBase = numero(item.kilosPorPaquete25kg ?? item.cantidad);
      const stock = obtenerStockMateria(item.producto);
      const unidadDestino = stock?.unidadMedida || item.unidadMedida || "kg";
      const kilosNecesarios = convertirUnidad(
        cantidadBase * numero(cantidadPaquetes),
        item.unidadMedida || unidadDestino,
        unidadDestino,
      );
      if (!fuente?.ingreso && !fuente?.lote) {
        return {
          producto: item.producto,
          kilosNecesarios,
          faltante: kilosNecesarios,
          unidadMedida: unidadDestino,
          ok: false,
          motivo: item.tipo === "Intermedio" ? "No se encontro orden intermedia disponible." : "No se encontro ingreso de mercaderia.",
        };
      }
      const disponible = numero(stock?.kilosDisponibles);
      if (disponible < kilosNecesarios) {
        return {
          producto: item.producto,
          kilosNecesarios,
          disponible,
          faltante: Math.round((kilosNecesarios - disponible) * 100) / 100,
          unidadMedida: unidadDestino,
          ok: false,
          motivo: `Stock insuficiente. Disponible: ${disponible} ${unidadDestino}.`,
        };
      }
      return {
        producto: item.producto,
        kilosNecesarios,
        disponible,
        ingresoCodigo: fuente.ingreso?.codigoTracking || fuente.lote?.codigo || "",
        unidadMedida: unidadDestino,
        ok: true,
      };
    });

    const faltantes = resultado.filter((item) => !item.ok);
    const insumosReceta = receta.flatMap((item) =>
      insumosDistribuidosDesdeReceta(item, cantidadPaquetes, productoProducido),
    );
    if (faltantes.length) {
      const encontrados = resultado.filter((item) => item.ok);
      onError?.("");
      onRecipeAlert?.({ encontrados, faltantes });
      setCampo("insumos", insumosReceta);
      return;
    }

    onRecipeAlert?.(null);
    onError?.("");
    setCampo("insumos", insumosReceta);
  };
  const seleccionarProducto = (value) => {
    const productoProducido = productosProducidos.find((item) => item.value === value);
    setCampo("productoTerminado", value);
    setCampo("ubicacionAsignada", ubicacionSugerida(value));
    setCampo("margenGananciaPorcentaje", productoProducido?.margenGananciaPorcentaje ?? 0);
    const rendimiento = numero(productoProducido?.recetaRendimiento?.cantidad);
    const cantidadRecetas = rendimiento > 0
      ? numero(formulario.cantidadProducir) / rendimiento
      : 0;
    aplicarReceta(value, cantidadRecetas);
  };
  const cambiarCantidad = (value) => {
    setCampo("cantidadProducir", value);
    const cantidadRecetas = cantidadRendimiento > 0 ? numero(value) / cantidadRendimiento : 0;
    aplicarReceta(formulario.productoTerminado, cantidadRecetas);
  };
  useEffect(() => {
    const insumosVacios = (formulario.insumos || []).length === 1 && !formulario.insumos[0]?.producto;
    if (formulario.productoTerminado && formulario.margenGananciaPorcentaje === "") {
      const productoProducido = productosProducidos.find(
        (item) => item.value === formulario.productoTerminado,
      );
      setCampo("margenGananciaPorcentaje", productoProducido?.margenGananciaPorcentaje ?? 0);
    }
    if (formulario.productoTerminado && numero(formulario.cantidadProducir) > 0 && insumosVacios) {
      const cantidadRecetas = cantidadRendimiento > 0
        ? numero(formulario.cantidadProducir) / cantidadRendimiento
        : 0;
      aplicarReceta(formulario.productoTerminado, cantidadRecetas);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formulario.productoTerminado, formulario.cantidadProducir]);
  const actualizarCamposInsumo = (index, campos) => {
    Object.entries(campos).forEach(([name, value]) => setItem("insumos", index, name, value));
  };
  const camposDeOrigen = ({ producto, proveedorOpcion, ingreso, lote, stock }) => {
    const esIntermedio = producto?.tipo === "Intermedio";
    const ingresoInicial = esIntermedio && lote?.origenTipo === "ingreso" ? ingresoDeLote(lote) : null;
    const usaIngresoInicial = Boolean(ingresoInicial);
    const tipoMedida = producto?.tipoMedida || stock?.tipoMedida || "Peso";
    const unidadMedida = stock?.unidadMedida || lote?.unidad || ingreso?.unidadCantidad || producto?.unidadMedida || "kg";
    return {
      origenTipo: esIntermedio && !usaIngresoInicial ? "produccion" : "mercaderia",
      mercaderiaId: esIntermedio ? ingresoInicial?._id || "" : ingreso?._id || "",
      ordenProduccionId: esIntermedio && !usaIngresoInicial ? lote?.id || "" : "",
      codigoIngreso: ingreso?.codigoTracking || ingresoInicial?.codigoTracking || lote?.codigo || "",
      productoId: producto?._id || "",
      producto: producto?.nombre || ingreso?.producto || "",
      tipoProducto: producto?.tipo || "",
      proveedorId: esIntermedio
        ? ""
        : proveedorOpcion?.proveedor?._id || idProveedorIngreso(ingreso),
      proveedor: esIntermedio
        ? usaIngresoInicial ? "Stock inicial" : "Produccion interna"
        : proveedorOpcion?.proveedor?.razonSocial || ingreso?.proveedor || "",
      lotePartidaNro: ingreso?.lotePartidaNro || ingresoInicial?.lotePartidaNro || lote?.lote || "",
      fechaElaboracion: dateInputValue(ingreso?.fechaElaboracion),
      fechaVencimiento: dateInputValue(ingreso?.fechaVencimiento || lote?.fechaVencimiento),
      tipoMedida,
      unidadMedida,
      unidadCosto: ingreso
        ? unidadPrecioProducto(tipoMedida)
        : lote?.unidadCosto || unidadPrecioProducto(tipoMedida),
      costoUnitario: ingreso || ingresoInicial
        ? precioCompraIngreso(ingreso || ingresoInicial)
        : lote
          ? numero(lote.costoUnitario)
          : numero(proveedorOpcion?.precio),
    };
  };
  const valorProveedorFuente = (producto, fuente) => {
    if (producto?.tipo === "Intermedio") {
      return fuente?.lote?.origenTipo === "ingreso" ? "stock-inicial" : "produccion-interna";
    }
    return String(fuente?.proveedorOpcion?.value || "");
  };
  const camposDeFuente = (producto, fuente) => camposDeOrigen({
    producto,
    proveedorOpcion: fuente?.proveedorOpcion,
    ingreso: fuente?.ingreso,
    lote: fuente?.lote,
    stock: fuente?.stock,
  });
  const distribuirFilaInsumo = (item, producto, fuentes) => {
    const cantidadTotal = numero(item.kilosNecesarios);
    if (!producto || cantidadTotal <= 0 || !fuentes.length) return [item];
    let restante = cantidadTotal;
    const filas = [];
    for (const fuente of fuentes) {
      if (restante <= 0.0000001) break;
      const disponible = disponibilidadFuente(fuente).cantidad;
      if (disponible <= 0) continue;
      const cantidadAsignada = Math.min(restante, disponible);
      filas.push({
        ...item,
        ...camposDeFuente(producto, fuente),
        kilosNecesarios: String(cantidadAsignada),
      });
      restante = Math.max(0, restante - cantidadAsignada);
    }
    if (restante > 0.0000001 && filas.length) {
      const ultima = filas[filas.length - 1];
      ultima.kilosNecesarios = String(numero(ultima.kilosNecesarios) + restante);
    }
    return filas.length ? filas : [item];
  };
  const reemplazarFilaInsumo = (index, filas) => {
    setCampo("insumos", [
      ...formulario.insumos.slice(0, index),
      ...filas,
      ...formulario.insumos.slice(index + 1),
    ]);
  };
  const fuentesConPreferencia = (producto, predicate) => {
    const fuentes = fuentesDisponiblesParaProducto(producto);
    return [
      ...fuentes.filter(predicate),
      ...fuentes.filter((fuente) => !predicate(fuente)),
    ];
  };
  const seleccionarProductoIngrediente = (index, value, option) => {
    onRecipeAlert?.(null);
    onError?.("");
    const producto = option?.producto;
    if (!producto) {
      actualizarCamposInsumo(index, {
        ...insumoVacio(),
        kilosNecesarios: formulario.insumos[index]?.kilosNecesarios || "",
      });
      return;
    }
    const fuente = obtenerFuenteParaProducto(producto.nombre, producto.tipo, producto._id);
    const itemActualizado = {
      ...formulario.insumos[index],
      ...camposDeOrigen({
        producto,
        proveedorOpcion: fuente?.proveedorOpcion,
        ingreso: fuente?.ingreso,
        lote: fuente?.lote,
        stock: fuente?.stock,
      }),
    };
    const fuentes = fuentesDisponiblesParaProducto(producto);
    reemplazarFilaInsumo(index, distribuirFilaInsumo(itemActualizado, producto, fuentes));
  };
  const seleccionarProveedorIngrediente = (index, value, option) => {
    onRecipeAlert?.(null);
    onError?.("");
    const item = formulario.insumos[index];
    const producto = productoCatalogoDe(item);
    if (!producto || !option) {
      actualizarCamposInsumo(index, {
        proveedorId: "",
        proveedor: "",
        mercaderiaId: "",
        ordenProduccionId: "",
        codigoIngreso: "",
        lotePartidaNro: "",
        fechaElaboracion: "",
        fechaVencimiento: "",
        costoUnitario: 0,
      });
      return;
    }

    if (producto.tipo === "Intermedio") {
      const fuentes = fuentesConPreferencia(
        producto,
        (fuente) => valorProveedorFuente(producto, fuente) === String(value),
      );
      reemplazarFilaInsumo(index, distribuirFilaInsumo(item, producto, fuentes));
      return;
    }

    const fuentes = fuentesConPreferencia(
      producto,
      (fuente) => String(fuente.proveedorOpcion?.value || "") === String(value),
    );
    reemplazarFilaInsumo(index, distribuirFilaInsumo(item, producto, fuentes));
  };
  const seleccionarIngresoIngrediente = (index, value) => {
    onRecipeAlert?.(null);
    onError?.("");
    const item = formulario.insumos[index];
    const producto = productoCatalogoDe(item);
    if (!producto) return;

    if (producto.tipo === "Intermedio") {
      const fuentes = fuentesConPreferencia(
        producto,
        (fuente) => String(fuente.lote?.id || "") === String(value),
      );
      reemplazarFilaInsumo(index, distribuirFilaInsumo(item, producto, fuentes));
      return;
    }

    const fuentes = fuentesConPreferencia(
      producto,
      (fuente) => String(fuente.ingreso?._id || "") === String(value),
    );
    reemplazarFilaInsumo(index, distribuirFilaInsumo(item, producto, fuentes));
  };
  const cambiarCantidadIngrediente = (index, value) => {
    const item = { ...formulario.insumos[index], kilosNecesarios: value };
    const producto = productoCatalogoDe(item);
    if (!producto || numero(value) <= 0) {
      setItem("insumos", index, "kilosNecesarios", value);
      return;
    }
    const origenId = item.origenTipo === "produccion" ? item.ordenProduccionId : item.mercaderiaId;
    const fuentes = fuentesConPreferencia(
      producto,
      (fuente) => String(
        producto.tipo === "Intermedio" ? fuente.lote?.id : fuente.ingreso?._id,
      ) === String(origenId || ""),
    );
    reemplazarFilaInsumo(index, distribuirFilaInsumo(item, producto, fuentes));
  };

  return (
    <>
      <section className="form-section">
        <h3>Producto terminado</h3>
        <div className="form-grid">
          <SearchableSelect label="Producto a producir" value={formulario.productoTerminado} onChange={(value) => seleccionarProducto(value)} options={productosProducidos} />
          <Field label={etiquetaCantidadOrden} type="text" inputMode="decimal" min="0.000001" step="any" value={formulario.cantidadProducir} onChange={(e) => cambiarCantidad(e.target.value)} />
          <Field label="Lote nro. / partida Nro" value={formulario.lotePartidaNro} onChange={(e) => setCampo("lotePartidaNro", e.target.value)} />
          <Field label="Ubicacion" value={formulario.ubicacionAsignada} onChange={(e) => setCampo("ubicacionAsignada", e.target.value)} />
          <Field label="Fecha de vencimiento" type="date" value={formulario.fechaVencimiento} onChange={(e) => setCampo("fechaVencimiento", e.target.value)} />
          <Field label="Fecha de produccion" type="date" value={formulario.fecha} onChange={(e) => setCampo("fecha", e.target.value)} />
        </div>
      </section>
      <section className="form-section">
        <div className="section-title-actions">
          <div>
            <h3>Ingredientes de la orden</h3>
          </div>
          <button className="btn" type="button" onClick={() => agregarItem("insumos", insumoVacio)}><Plus size={17} />Agregar</button>
        </div>
        <RecetaResultado alerta={recipeAlert} />
        <div className="ops-line-list">
          {formulario.insumos.map((item, index) => {
            const producto = productoCatalogoDe(item);
            const opcionesProveedor = proveedoresParaProducto(producto);
            const proveedorValue =
              item.proveedorId ||
              opcionesProveedor.find(
                (opcion) =>
                  normalizarProducto(opcion.proveedor?.razonSocial || opcion.label) ===
                  normalizarProducto(item.proveedor),
              )?.value ||
              "";
            const opcionesIngreso = producto?.tipo === "Intermedio"
              ? lotesParaIntermedio(producto, proveedorValue).lotes.map((lote) => ({
                  value: lote.id,
                  label: `${lote.codigo || "OP"} - Lote ${lote.lote || "-"} - ${numero(
                    lote.disponible,
                  ).toLocaleString("es-AR")} ${lote.unidad || producto.unidadMedida} disponibles`,
                }))
              : ingresosParaProductoProveedor(producto, proveedorValue).flatMap((ingreso) => {
                  const lote = loteStockDeIngreso(producto, ingreso._id);
                  if (numero(lote?.disponible) <= 0) return [];
                  return [{
                    value: ingreso._id,
                    label: `${ingreso.codigoTracking || "Sin codigo"} - ${numero(
                      lote.disponible,
                    ).toLocaleString("es-AR")} ${lote.unidad || producto.unidadMedida} disponibles`,
                  }];
                });
            const ingresoValue =
              item.origenTipo === "produccion" ? item.ordenProduccionId : item.mercaderiaId;

            return (
              <div className="ops-line production-ingredient-line" key={item._id || index}>
                <SearchableSelect
                  label="Producto"
                  value={producto?._id || item.productoId}
                  onChange={(value, option) => seleccionarProductoIngrediente(index, value, option)}
                  options={productosIngredientes}
                  placeholder="Buscar producto..."
                />
                <SelectInput
                  label="Proveedor"
                  value={proveedorValue}
                  onChange={(event) => {
                    const value = event.target.value;
                    const option = opcionesProveedor.find(
                      (opcion) => String(opcion.value) === String(value),
                    );
                    seleccionarProveedorIngrediente(index, value, option);
                  }}
                  options={opcionesProveedor}
                  disabled={!producto}
                />
                <SelectInput
                  label={producto?.tipo === "Intermedio" ? "Orden de produccion" : "Codigo de ingreso"}
                  value={ingresoValue || ""}
                  onChange={(event) => seleccionarIngresoIngrediente(index, event.target.value)}
                  options={opcionesIngreso}
                  disabled={!producto || !proveedorValue}
                />
                <Field label="Lote / Partida Nro" value={item.lotePartidaNro || ""} disabled />
                <Field label="Fecha de vto" type="date" value={dateInputValue(item.fechaVencimiento)} disabled />
                <Field label={`Cantidad necesaria ${item.unidadMedida || "kg"}`} type="text" inputMode="decimal" step="0.001" value={item.kilosNecesarios} onChange={(e) => cambiarCantidadIngrediente(index, e.target.value)} />
                <Field
                  label={`Costo unitario por 1 ${etiquetaUnidadCosto(item.unidadCosto)}`}
                  value={formatoPrecio(item.costoUnitario)}
                  disabled
                />
                <Field label="Costo total" value={formatoPrecio(costoTotalIngrediente(item))} disabled />
                <button className="btn btn-danger product-remove" type="button" onClick={() => quitarItem("insumos", index, insumoVacio)}><XCircle size={16} /></button>
              </div>
            );
          })}
        </div>
      </section>
      <section className="form-section">
        <div className="section-title-actions">
          <h3>Materiales utilizados</h3>
          <button className="btn" type="button" onClick={() => agregarItem("materialesEmpaque", materialEmpaqueVacio)}>
            <Plus size={17} />
            Agregar
          </button>
        </div>
        <div className="ops-line-list">
          {(formulario.materialesEmpaque || [materialEmpaqueVacio()]).map((item, index) => (
            <div className="ops-line packaging-line" key={index}>
              <SearchableSelect
                label="Material"
                value={item.materialId}
                options={(materialesProduccion || []).map((material) => ({
                  value: material._id,
                  label: `${material.nombre} (${Number(material.stockActual || 0).toLocaleString("es-AR")} disponibles)`,
                  material,
                }))}
                onChange={(value, option) => {
                  const material = option?.material;
                  setItem("materialesEmpaque", index, "materialId", value);
                  setItem("materialesEmpaque", index, "nombre", material?.nombre || "");
                  setItem("materialesEmpaque", index, "descripcion", material?.descripcion || "");
                  setItem("materialesEmpaque", index, "precioUnitario", material?.precio ?? "");
                  setItem("materialesEmpaque", index, "precioTotal", numero(item.cantidad) * numero(material?.precio));
                }}
                placeholder="Buscar material..."
              />
              <Field label="Cantidad" type="text" inputMode="decimal" step="1" min="0" value={item.cantidad} onChange={(e) => setItem("materialesEmpaque", index, "cantidad", e.target.value)} />
              <Field
                label="Stock disponible"
                value={`${Number(
                  (materialesProduccion || []).find((material) => String(material._id) === String(item.materialId))?.stockActual || 0,
                ).toLocaleString("es-AR")} unidades`}
                disabled
              />
              <Field label="Precio unitario" value={formatoPrecio(item.precioUnitario)} disabled />
              <Field label="Precio total" value={formatoPrecio(numero(item.cantidad) * numero(item.precioUnitario))} disabled />
              <button className="btn btn-danger product-remove" type="button" onClick={() => quitarItem("materialesEmpaque", index, materialEmpaqueVacio)}>
                <XCircle size={16} />
              </button>
            </div>
          ))}
        </div>
        <div className="ops-total">
          Total materiales: <strong>{formatoPrecio((formulario.materialesEmpaque || []).reduce(
            (total, item) => total + numero(item.cantidad) * numero(item.precioUnitario),
            0,
          ))}</strong>
        </div>
      </section>
      <section className="form-section">
        <h3>Calculador de costos</h3>
        <div className="production-cost-controls">
          <Field
            label="Margen de ganancia (%)"
            type="text"
            inputMode="decimal"
            min="0"
            step="0.01"
            value={formulario.margenGananciaPorcentaje}
            onChange={(event) => setCampo("margenGananciaPorcentaje", event.target.value)}
          />
        </div>
        <div className="order-detail-summary production-cost-summary">
          <DetailItem
            label={`Costo de produccion de 1 ${unidadCostoProduccion}`}
            value={formatoPrecio(costoProduccionPorUnidad)}
          />
          <DetailItem
            label={`Costo de ${cantidadProducidaCosto.toLocaleString("es-AR")} ${unidadCostoProduccion} producidos`}
            value={formatoPrecio(costoIngredientes)}
          />
          <DetailItem label="Costo de materiales" value={formatoPrecio(costoMateriales)} />
          <DetailItem label="Costo total" value={formatoPrecio(costoTotalOrden)} />
          <DetailItem
            label={`Margen (${margenGananciaPorcentaje.toLocaleString("es-AR")}%)`}
            value={formatoPrecio(importeMargen)}
          />
          <DetailItem label="Precio de venta calculado" value={formatoPrecio(precioVentaCalculado)} />
        </div>
      </section>
    </>
  );
}

function FraccionadoForm({ formulario, productosTerminados, productosCatalogo, ordenesProduccion, stocks, setCampo, setItem, agregarItem, quitarItem }) {
  const total = formulario.salidas.reduce((sum, item) => sum + kgSalida(item), 0);
  const productoCatalogo = (productosCatalogo || []).find(
    (producto) => normalizarProducto(producto.nombre) === normalizarProducto(formulario.producto),
  );
  const esVolumen = productoCatalogo?.tipoMedida === "Volumen";
  const unidadMayor = esVolumen ? "l" : "kg";
  const unidadMenor = esVolumen ? "ml" : "g";
  const presentacionesConfiguradas = (productoCatalogo?.presentacionesFraccionamiento || []).map((item) => ({
    value: convertirUnidad(item.cantidad, item.unidadMedida, unidadMenor),
    label: `${numero(item.cantidad).toLocaleString("es-AR")} ${item.unidadMedida}`,
  })).filter((item) => item.value > 0);
  const opcionesPresentacion = presentacionesConfiguradas.length
    ? presentacionesConfiguradas
    : presentaciones.map((cantidad) => ({
        value: cantidad,
        label: esVolumen
          ? `${cantidad >= 1000 ? cantidad / 1000 : cantidad} ${cantidad >= 1000 ? "l" : "ml"}`
          : formatPresentacion(cantidad),
      }));
  const salidaConfiguradaVacia = () => ({
    presentacionGramos: opcionesPresentacion[0]?.value || 1000,
    cantidadPaquetes: "",
  });
  const kilosDisponiblesOrden = (orden) => {
    if (orden.kilosDisponibles !== undefined && orden.kilosDisponibles !== null) return numero(orden.kilosDisponibles);
    return numero(orden.kilosProducidos || orden.cantidadPaquetes25kg * (orden.cantidadPorPaquete || 25));
  };
  const ordenesProducto = (ordenesProduccion || [])
    .filter((orden) =>
      orden.estado === "finalizada" &&
      normalizarProducto(orden.productoTerminado) === normalizarProducto(formulario.producto) &&
      kilosDisponiblesOrden(orden) > 0,
    )
    .map((orden) => ({
      value: orden._id,
      label: `${orden.codigo || "OP"} - ${formatDate(orden.fecha)} - ${kilosDisponiblesOrden(orden).toLocaleString("es-AR")} ${orden.unidadMedida || "kg"} disponibles`,
      orden,
    }));
  const stockTerminado = (stocks?.terminados || []).find(
    (item) => normalizarProducto(item.producto) === normalizarProducto(formulario.producto),
  );
  const opcionesStockInicial = (stockTerminado?.lotes || [])
    .filter((lote) => lote.origenTipo === "ingreso" && numero(lote.disponible) > 0)
    .map((lote) => {
      const origen = {
        _id: lote.id,
        codigo: lote.codigo || "Stock inicial",
        kilosDisponibles: numero(lote.disponible),
        kilosProducidos: numero(lote.cantidad),
        unidadMedida: lote.unidad || productoCatalogo?.unidadMedida || unidadMayor,
        lotePartidaNro: lote.lote || "",
        fechaVencimiento: "",
        esStockInicial: true,
      };
      return {
        value: lote.id,
        label: `${origen.codigo} - Stock inicial - ${origen.kilosDisponibles.toLocaleString("es-AR")} ${origen.unidadMedida} disponibles`,
        orden: origen,
      };
    });
  const origenesProducto = [...opcionesStockInicial, ...ordenesProducto];
  const ordenSeleccionada = origenesProducto.find((option) => String(option.value) === String(formulario.ordenProduccionId || ""))?.orden;
  const saldoSeleccionado = ordenSeleccionada ? kilosDisponiblesOrden(ordenSeleccionada) : 0;
  const seleccionarProducto = (value) => {
    setCampo("producto", value);
    setCampo("ubicacionAsignada", ubicacionSugerida(value));
    setCampo("ordenProduccionId", "");
    setCampo("codigoProduccion", "");
    setCampo("rnpa", "");
    setCampo("lotePartidaNro", "");
    setCampo("fechaElaboracion", "");
    setCampo("fechaVencimiento", "");
    const seleccionado = (productosCatalogo || []).find(
      (producto) => normalizarProducto(producto.nombre) === normalizarProducto(value),
    );
    const menor = seleccionado?.tipoMedida === "Volumen" ? "ml" : "g";
    const primera = (seleccionado?.presentacionesFraccionamiento || [])[0];
    setCampo("salidas", [{
      presentacionGramos: primera ? convertirUnidad(primera.cantidad, primera.unidadMedida, menor) : 1000,
      cantidadPaquetes: "",
    }]);
  };
  const seleccionarOrdenProduccion = (value, option) => {
    const orden = option?.orden;
    setCampo("ordenProduccionId", value);
    setCampo("codigoProduccion", orden?.codigo || "");
    setCampo("rnpa", orden?.rnpa || "");
    setCampo("lotePartidaNro", orden?.lotePartidaNro || "");
    setCampo("fechaElaboracion", "");
    setCampo("fechaVencimiento", dateInputValue(orden?.fechaVencimiento));
  };

  return (
    <>
      <section className="form-section">
        <h3>Producto terminado a fraccionar</h3>
        <div className="form-grid">
          <SearchableSelect label="Producto terminado" value={formulario.producto} onChange={(value) => seleccionarProducto(value)} options={productosTerminados} />
          <SearchableSelect label="Origen de stock" value={formulario.ordenProduccionId} onChange={seleccionarOrdenProduccion} options={origenesProducto} />
           <Field label={`Cantidad ${unidadMayor}`} type="text" inputMode="decimal" step="0.01" value={total ? String(total) : ""} disabled />
          <Field label="Lote nro. / partida Nro" value={formulario.lotePartidaNro} onChange={(e) => setCampo("lotePartidaNro", e.target.value)} />
          <Field label="Ubicacion" value={formulario.ubicacionAsignada} onChange={(e) => setCampo("ubicacionAsignada", e.target.value)} />
          <Field label="Fecha de elaboracion" type="date" value={formulario.fechaElaboracion} onChange={(e) => setCampo("fechaElaboracion", e.target.value)} />
          <Field label="Fecha de vencimiento" type="date" value={formulario.fechaVencimiento} onChange={(e) => setCampo("fechaVencimiento", e.target.value)} />
          <Field label="Fecha" type="date" value={formulario.fecha} onChange={(e) => setCampo("fecha", e.target.value)} />
        </div>
      </section>
      <section className="form-section">
        <div className="section-title-actions">
          <h3>Paquetes resultantes</h3>
          <button className="btn" type="button" onClick={() => agregarItem("salidas", salidaConfiguradaVacia)}><Plus size={17} />Agregar</button>
        </div>
        {ordenSeleccionada && total > saldoSeleccionado && (
          <div className="alert alert-error">
             La orden de produccion seleccionada tiene {saldoSeleccionado.toLocaleString("es-AR")} {unidadMayor} disponibles y el fraccionamiento requiere {total.toLocaleString("es-AR")} {unidadMayor}.
          </div>
        )}
        <div className="ops-line-list">
          {formulario.salidas.map((item, index) => (
            <div className="ops-line" key={index}>
              <SelectInput label="Presentacion" value={item.presentacionGramos} onChange={(e) => setItem("salidas", index, "presentacionGramos", e.target.value)} options={opcionesPresentacion} />
              <Field label="Paquetes" type="text" inputMode="decimal" step="1" value={item.cantidadPaquetes} onChange={(e) => setItem("salidas", index, "cantidadPaquetes", e.target.value)} />
              <div className="ops-line-total">{kgSalida(item).toLocaleString("es-AR")} {unidadMayor}</div>
              <button className="btn btn-danger product-remove" type="button" onClick={() => quitarItem("salidas", index, salidaVacia)}><XCircle size={16} /></button>
            </div>
          ))}
        </div>
        <div className="ops-total">Total a descontar de terminados: <strong>{total.toLocaleString("es-AR")} {unidadMayor}</strong></div>
      </section>
    </>
  );
}
